<template>
  <div class="config-root">
    <div class="config-page">
      <header class="card card-custom config-header">
        <div class="config-header-body">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label font-weight-bolder text-dark">
              Configurações gerais
            </span>
            <small class="d-block text-muted font-size-sm mt-2">
              Gerencie as preferências dos módulos e recursos do sistema.
            </small>
          </h3>
          <div
            v-if="autosaveEstado"
            class="config-autosave-status"
            :class="{ 'config-autosave-error': autosaveEstado === 'erro' }"
            role="status"
            aria-live="polite"
          >
            {{ autosaveMensagem }}
            <button
              v-if="autosaveEstado === 'erro'"
              type="button"
              class="config-autosave-retry"
              @click="flushAutosave"
            >
              Tentar novamente
            </button>
          </div>
        </div>
        <div ref="configSearch" class="config-search">
          <label class="sr-only" for="config-search-input">Buscar configurações</label>
          <input
            id="config-search-input"
            v-model="buscaConfiguracao"
            class="form-control config-search-input"
            type="search"
            placeholder="Buscar configuração, módulo ou recurso..."
            autocomplete="off"
            :aria-expanded="buscaAberta && !!buscaConfiguracao"
            aria-controls="config-search-results"
            @focus="buscaAberta = true"
            @input="buscaAberta = true"
            @keydown.esc.stop.prevent="fecharBusca"
            @keydown.enter.prevent="abrirPrimeiroResultadoBusca"
          />
          <div
            v-if="buscaAberta && buscaConfiguracao.trim()"
            id="config-search-results"
            class="config-search-dropdown"
            role="listbox"
            aria-label="Resultados da busca de configurações"
          >
            <button
              v-for="resultado in resultadosBusca"
              :key="resultado.key"
              type="button"
              class="config-search-result"
              role="option"
              @click="abrirResultadoBusca(resultado)"
            >
              <span class="config-search-result-label">{{ resultado.label }}</span>
              <small>{{ resultado.sectionName }}<template v-if="resultado.group"> › {{ resultado.group }}</template></small>
            </button>
            <div v-if="!resultadosBusca.length" class="config-search-empty">
              Nenhuma configuração encontrada.
            </div>
          </div>
        </div>
      </header>
      <div class="config-shell">
        <nav class="card config-sidebar" aria-label="Seções das configurações gerais">
          <button
            v-for="(aba, indice) in abasConfiguracoes"
            :key="aba.chave"
            type="button"
            class="config-sidebar-item"
            :class="{ 'config-sidebar-item-active': abaConfiguracaoAtiva === indice }"
            :aria-current="abaConfiguracaoAtiva === indice ? 'page' : null"
            @click="selecionarAbaConfiguracao(indice)"
          >
            {{ aba.titulo }}
          </button>
        </nav>
        <div class="card config-mobile-nav">
          <label for="config-secao-mobile">Seção</label>
          <select
            id="config-secao-mobile"
            :value="abaConfiguracaoAtiva"
            class="form-control"
            @change="selecionarAbaConfiguracao(Number($event.target.value), $event.target)"
          >
            <option
              v-for="(aba, indice) in abasConfiguracoes"
              :key="aba.chave"
              :value="indice"
            >
              {{ aba.titulo }}
            </option>
          </select>
        </div>
        <div class="config-main">
          <div class="config-content-frame">
            <div class="config-content-body">
        <b-tabs v-model="abaConfiguracaoAtiva" class="config-tabs">
          <b-tab no-body title="NFe/NFCe">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="descontar_icms_base_piscofins">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar desconto de ICMS na base do PIS/COFINS
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="descontar_icms_base_piscofins"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="vendas_cartao_conta_receber">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Vendas na NFCe no cartão devem ir para Contas a Receber?
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="vendas_cartao_conta_receber"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row config-row-caixa" data-config-key="abertura_caixa">
                <b-row>
                  <b-col md="3">
                    <p class="font-weight-bolder text-dark mb-2">
                      Abertura de caixa
                    </p>
                    <span class="switch switch-icon">
                      <label>
                        <input type="checkbox" v-model="abertura_caixa" />
                        <span></span>
                      </label>
                    </span>
                  </b-col>
                  <b-col md="3" v-if="abertura_caixa" class="config-dependent-fields">
                    <p class="font-weight-bolder text-dark mb-2">
                      Fechamento às cegas
                    </p>
                    <span class="switch switch-icon">
                      <label>
                        <input type="checkbox" v-model="fechamento_as_cegas" />
                        <span></span>
                      </label>
                    </span>
                  </b-col>
                </b-row>
              </div>
              <div class="config-row" data-config-key="calcular_st_desconto_icms">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Calcular ST considerando desconto do ICMS
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="calcular_st_desconto_icms"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="importar_obs_pessoa">
                <b-col md="5">
                  <p class="font-weight-bolder text-dark mb-2">
                    Importar OBS do cadastro do cliente para NFCe/Pré-Venda
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="importar_obs_pessoa" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="desconto_incondicional">
                <b-col md="3">
                  <p class="font-weight-bolder text-dark mb-2">
                    Desconto Incondicional
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="desconto_incondicional" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="ordenar_item_adicionado_por_ultimo_pdv">
                <b-col md="5">
                  <p class="font-weight-bolder text-dark mb-2">
                    Ordenar item adicionado por último no PDV
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="ordenar_item_adicionado_por_ultimo_pdv"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="somar_frete_despesas_baseicms">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Somar Frete/Seguro/Outras/IPI despesas na Base ICMS
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="somar_frete_despesas_baseicms"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="integracao_pos">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Integração com POS
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="integracao_pos" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row config-row-natureza" data-config-key="fixar_natureza_nfe">
                <b-row>
                  <b-col md="4">
                    <p class="font-weight-bolder text-dark mb-2">
                      Fixar natureza padrão na NFe
                    </p>
                    <span class="switch switch-icon">
                      <label>
                        <input type="checkbox" v-model="fixar_natureza_nfe" />
                        <span></span>
                      </label>
                    </span>
                  </b-col>
                  <b-col md="6" v-if="fixar_natureza_nfe" class="config-dependent-fields">
                    <b-form-group
                      label="Natureza padrão da NFe"
                      label-for="natureza-nfe"
                    >
                      <VueSelect
                        id="natureza-nfe"
                        :filterable="false"
                        :options="naturezas_nfe"
                        v-model="natureza_nfe"
                        @search="onSearchNaturezaNFe"
                      >
                        <template slot="no-options">
                          Informe a natureza
                        </template>
                        <template slot="option" slot-scope="option">
                          <div class="d-center">
                            {{ option.descricao }} - {{ option.cfop.codigo }}
                          </div>
                        </template>
                        <template slot="selected-option" slot-scope="option">
                          <div class="selected d-center">
                            {{ option.descricao }}
                          </div>
                        </template>
                      </VueSelect>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
              <div class="config-row" data-config-key="nao_converter_cfop">
                <b-col md="3">
                  <p class="font-weight-bolder text-dark mb-2">
                    Não converter CFOP
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="nao_converter_cfop" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="vendedor_obrigatorio">
                <b-col md="4">
                  <p class="font-weight-bolder text-dark mb-2">
                    Obrigatório informar vendedor NFCe/Pré-Venda
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="vendedor_obrigatorio" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="importar_obs_produto">
                <b-col md="4">
                  <p class="font-weight-bolder text-dark mb-2">
                    Importar OBS do cadastro do produto para NFe
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="importar_obs_produto" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="nao_movimentar_estoque">
                <b-col md="4">
                  <p class="font-weight-bolder text-dark mb-2">
                    Não movimentar estoque na Pré-venda
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="nao_movimentar_estoque" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="lucro_pis_cofins">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Gerar valores de PIS/COFINS com base no lucro da venda
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="lucro_pis_cofins" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="padrao_reforma_tributaria">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Utilizar padrão CBS/IBS - Reforma Tributária
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="padrao_reforma_tributaria"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="usar_codigo_balanca">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Utilizar código da balança
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="usar_codigo_balanca" />
                      <span></span>
                    </label>
                  </span>
                  <small v-if="!usar_codigo_balanca" class="text-muted d-block mt-2">
                    Etiquetas geradas pela carga de balança continuam sendo lidas no formato padrão.
                  </small>
                </b-col>
                <b-row v-if="usar_codigo_balanca" class="config-dependent-fields config-dependent-grid">
                  <b-col md="4">
                    <label for="balanca-tamanho-produto" class="font-weight-bolder text-dark">
                      Dígitos do código do produto
                    </label>
                    <b-form-input
                      id="balanca-tamanho-produto"
                      v-model.number="balanca_tamanho_codigo_produto"
                      type="number"
                      min="1"
                      max="10"
                      step="1"
                    />
                    <small class="text-muted">Após o prefixo 2 da etiqueta.</small>
                  </b-col>
                  <b-col md="4">
                    <label class="font-weight-bolder text-dark d-block">Etiqueta com</label>
                    <b-form-radio-group v-model="balanca_etiqueta_tipo">
                      <b-form-radio value="peso">Peso</b-form-radio>
                      <b-form-radio value="preco">Preço</b-form-radio>
                    </b-form-radio-group>
                    <small class="text-muted">
                      Preço em centavos; peso em kg conforme as casas decimais.
                    </small>
                  </b-col>
                  <b-col v-if="balanca_etiqueta_tipo === 'peso'" md="4">
                    <label for="balanca-casas-decimais" class="font-weight-bolder text-dark">
                      Casas decimais do peso (kg)
                    </label>
                    <b-form-input
                      id="balanca-casas-decimais"
                      v-model.number="balanca_casas_decimais"
                      type="number"
                      min="0"
                      max="5"
                      step="1"
                    />
                  </b-col>
                </b-row>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Compras">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="ultimo_nsu">
                <b-col md="3" class="config-inline-short-value">
                  <p class="font-weight-bolder text-dark mb-2">
                    Último NSU - Monitor NFe
                  </p>
                  <b-form-input v-model="ultimo_nsu" class="config-input-nsu"> </b-form-input>
                </b-col>
              </div>
              <div class="config-row" data-config-key="gerar_conta_pagar">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Todas as compras devem gerar as parcelas em
                    Financeiro/Contas a pagar
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="gerar_conta_pagar" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="considerar_qtd_tributavel">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar quantidade tributável na importação do XML
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="considerar_qtd_tributavel"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="importar_desc_complementar">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Importar informações adicionais do item de forma automática
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="importar_desc_complementar"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="considerar_data_entrada">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar a data de entrada na criação das contas a pagar
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="considerar_data_entrada"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Pedidos">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="impressao_cliente_pedido_producao">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Não imprimir dados do cliente para produção
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="impressao_cliente_pedido_producao"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="considerar_pedidos_faturados">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar Pedidos faturados no Dashboard
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="considerar_pedidos_faturados"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="considerar_obs_produto_pedido">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar OBS do produto no Pedido
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="considerar_obs_produto_pedido" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="mensagem_adicional_pedido">
                <b-col md="12" class="config-message-line">
                  <p class="font-weight-bolder text-dark mb-2">
                    Incluir mensagem adicional no Pedido
                  </p>
                  <button
                    v-if="mensagem_adicional_pedido"
                    type="button"
                    class="btn btn-light-primary font-weight-bolder config-message-action"
                    @click="abrirModalMensagem('pedido')"
                  >
                    Carregar mensagem
                  </button>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="mensagem_adicional_pedido" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Orçamentos">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="considerar_obs_produto_orcamento">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar OBS do produto no Orçamento
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="considerar_obs_produto_orcamento" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="mensagem_adicional_orcamento">
                <b-col md="12" class="config-message-line">
                  <p class="font-weight-bolder text-dark mb-2">
                    Incluir mensagem adicional no Orçamento
                  </p>
                  <button
                    v-if="mensagem_adicional_orcamento"
                    type="button"
                    class="btn btn-light-primary font-weight-bolder config-message-action"
                    @click="abrirModalMensagem('orcamento_mensagem')"
                  >
                    Carregar mensagem
                  </button>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="mensagem_adicional_orcamento" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="permitir_servicos_orcamento">
                <b-col md="3">
                  <p class="font-weight-bolder text-dark mb-2">
                    Permitir serviços no Orçamento
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="permitir_servicos_orcamento"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="orcamento_com_foto">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Orçamento com foto
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="orcamento_com_foto" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="apresentar_objeto_identificador_orcamento">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Apresentar objeto/identificador no orçamento
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="apresentar_objeto_identificador_orcamento"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="fixar_observacao_orcamento">
                <b-col md="12" class="config-message-line">
                  <p class="font-weight-bolder text-dark mb-2">
                    Fixar observações nos PDFs
                  </p>
                  <button
                    v-if="fixar_observacao_orcamento"
                    type="button"
                    class="btn btn-light-primary font-weight-bolder config-message-action"
                    @click="abrirModalMensagem('orcamento')"
                  >
                    Carregar mensagem
                  </button>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="fixar_observacao_orcamento" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="custo_operacional">
                <b-col md="12">
                  <p class="font-weight-bolder text-dark mb-2">
                    Custo Operacional
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="custo_operacional" />
                      <span></span>
                    </label>
                  </span>
                  <div v-if="custo_operacional" class="config-dependent-fields">
                    <b-form-group
                      id="custo_percentual"
                      label-for="custo_percentual"
                      label="Custo %"
                    >
                      <money
                        class="form-control config-input-short"
                        v-model="custo_percentual"
                        v-bind="configMoney"
                      >
                      </money>
                    </b-form-group>
                  </div>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="OS">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="fixar_observacao_os">
                <b-col md="12" class="config-message-line">
                  <p class="font-weight-bolder text-dark mb-2">
                    Fixar observações nos PDFs
                  </p>
                  <button
                    v-if="fixar_observacao_os"
                    type="button"
                    class="btn btn-light-primary font-weight-bolder config-message-action"
                    @click="abrirModalMensagem('os')"
                  >
                    Carregar mensagem
                  </button>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="fixar_observacao_os" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="trabalhar_horas_os">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Utilizar controle de horas no lançamento da OS
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="trabalhar_horas_os" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="trabalhar_numero_interno_os">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Utilizar número interno na OS
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="trabalhar_numero_interno_os"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="considerar_os_faturadas_nfse_feitas">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Considerar OS faturadas e NFSe feitas no Dashboard
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="considerar_os_faturadas_nfse_feitas"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="NFSe" v-if="userAccessStore.access.nfse">
            <b-card-footer class="config-tab-body">
              <div class="config-column-card config-column-card-single">
              <div class="config-row" data-config-key="informacao_nfse">
                <b-col md="12">
                  <p class="font-weight-bolder text-dark mb-2">
                    Informação complementar fixa
                  </p>
                  <b-form-input
                    maxlength="100"
                    v-model="informacao_nfse"
                  ></b-form-input>
                </b-col>
              </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Restaurante" v-if="podeExibirRestaurante">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="restaurante-canais">
                <b-col md="10">
                  <div
                    class="d-flex flex-column flex-md-row align-items-md-center justify-content-between"
                  >
                    <div>
                      <p class="font-weight-bolder text-dark mb-2">
                        Canais digitais do restaurante
                      </p>
                      <p class="text-muted font-size-sm mb-2">
                        Cardápio Digital:
                        <strong
                          :class="
                            restaurante_cardapio_online_ativo
                              ? 'text-success'
                              : 'text-muted'
                          "
                        >
                          {{
                            restaurante_cardapio_online_ativo
                              ? "ativo"
                              : "inativo"
                          }}
                        </strong>
                        · Self Pedido:
                        <strong
                          :class="
                            restaurante_self_pedido_ativo
                              ? 'text-success'
                              : 'text-muted'
                          "
                        >
                          {{
                            restaurante_self_pedido_ativo ? "ativo" : "inativo"
                          }}
                        </strong>
                      </p>
                      <p class="text-muted font-size-sm mb-0">
                        Ativação, catálogo, horários, entrega, pagamento e
                        aparência agora são administrados em um único hub.
                      </p>
                    </div>
                    <router-link
                      class="btn btn-light-primary font-weight-bold mt-4 mt-md-0"
                      :to="{
                        name: 'restauranteCardapioOnline',
                        query: { secao: 'service', aba: 'channels' },
                      }"
                    >
                      Abrir Cardápio Digital
                    </router-link>
                  </div>
                </b-col>
              </div>
              <div class="config-row" data-config-key="restaurante_producao_etapa_unica">
                <b-col md="8">
                  <p class="font-weight-bolder text-dark mb-2">
                    Etapa única na produção do restaurante
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="restaurante_producao_etapa_unica"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="restaurante_usar_apenas_comandas">
                <b-col md="8">
                  <p class="font-weight-bolder text-dark mb-2">
                    Usar apenas comandas
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="restaurante_usar_apenas_comandas"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="restaurante_impressao_qz_tray">
                <b-col md="8">
                  <p class="font-weight-bolder text-dark mb-2">
                    Habilitar impressão automática dos pedidos
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="restaurante_impressao_qz_tray"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Estoque">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="verifica_estoque">
                <b-col md="3">
                  <p class="font-weight-bolder text-dark mb-2">
                    Verificar estoque nas vendas
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="verifica_estoque" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="desconsiderar_estoque_condicional">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Desconsiderar estoque em condicionais
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="desconsiderar_estoque_condicional"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Gerais">
            <b-card-footer class="config-tab-body">
              <div class="config-column-layout">
                <div class="config-column-card">
              <div class="config-row" data-config-key="verifica_cadastro_pessoa">
                <b-col md="4">
                  <p class="font-weight-bolder text-dark mb-2">
                    Verificar se a pessoa já está cadastrada
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="verifica_cadastro_pessoa"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="juros_simples">
                <b-col md="4">
                  <p class="font-weight-bolder text-dark mb-2">
                    Calcular Juros Simples
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="juros_simples" />
                      <span></span>
                    </label>
                  </span>
                  <div
                    v-if="juros_simples"
                    class="config-dependent-fields config-dependent-grid"
                    data-config-key="taxa_diaria"
                  >
                    <div>
                      <b-form-group
                        id="taxa_diaria"
                        label-for="taxa_diaria"
                        label="Taxa Mensal %"
                      >
                        <money
                          class="form-control config-input-short"
                          v-model="taxa_diaria"
                          v-bind="configMoney"
                        >
                        </money>
                      </b-form-group>
                    </div>
                    <div>
                      <b-form-group id="multa" label-for="multa" label="Multa %">
                        <money
                          class="form-control config-input-short"
                          v-model="multa"
                          v-bind="configMoney"
                        >
                        </money>
                      </b-form-group>
                    </div>
                  </div>
                </b-col>
              </div>
              <div class="config-row" data-config-key="contas_marcadas_color">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Cor Contas marcadas
                  </p>
                  <label v-b-tooltip.hover.right="'Cor das contas marcadas'">
                    <input type="color" v-model="contas_marcadas_color" />
                    <span></span>
                  </label>
                </b-col>
              </div>
              <div class="config-row" data-config-key="casas_decimal_preco">
                <b-col md="3" class="config-inline-short-value">
                  <p class="font-weight-bolder text-dark mb-2">
                    Casas decimais preço
                  </p>
                  <b-form-select
                    placeholder=""
                    :options="casaDecimaisOptions"
                    v-model="casas_decimal_preco"
                  />
                </b-col>
              </div>
              <div class="config-row" data-config-key="casas_decimal_quantidade">
                <b-col md="3" class="config-inline-short-value">
                  <p class="font-weight-bolder text-dark mb-2">
                    Casas decimais quantidade
                  </p>
                  <b-form-select
                    placeholder=""
                    :options="casaDecimaisOptions"
                    v-model="casas_decimal_quantidade"
                  />
                </b-col>
              </div>
              <div class="config-row" data-config-key="solicita_senha_adm">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Solicitar senha de Administrador
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="solicita_senha_adm" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="checar_codigo_barras_duplicado">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Checar código de barras duplicado
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="checar_codigo_barras_duplicado" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
                <div class="config-column-card">
              <div class="config-row" data-config-key="utilizar_sempre_referencia">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Utilizar sempre o campo referência para pesquisar os
                    produtos
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="utilizar_sempre_referencia"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="sync_vendizap">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Sincronizar os dados de cadastros com VendiZap
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="sync_vendizap" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="relatorios_html">
                <b-col md="3">
                  <p class="font-weight-bolder text-dark mb-2">
                    Definir formato (.html) como padrão dos relatórios
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input type="checkbox" v-model="relatorios_html" />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
              <div class="config-row" data-config-key="bloquear_venda_cliente_inadimplente">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Bloquear venda para cliente com inadimplência
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="bloquear_venda_cliente_inadimplente"
                      />
                      <span></span>
                    </label>
                  </span>
                  <div
                    v-if="bloquear_venda_cliente_inadimplente"
                    class="config-dependent-fields"
                  >
                    <b-form-group
                      id="limite_inadimplencia_permitida"
                      label-for="limite_inadimplencia_permitida"
                      label="Valor limite permitido"
                    >
                      <money
                        class="form-control config-input-short"
                        id="limite_inadimplencia_permitida"
                        v-model="limite_inadimplencia_permitida"
                        v-bind="configMoney"
                      >
                      </money>
                    </b-form-group>
                  </div>
                </b-col>
              </div>
              <div class="config-row" data-config-key="utilizar_sempre_descricao">
                <b-col md="6">
                  <p class="font-weight-bolder text-dark mb-2">
                    Utilizar sempre o campo descrição para pesquisar os produtos
                  </p>
                  <span class="switch switch-icon">
                    <label>
                      <input
                        type="checkbox"
                        v-model="utilizar_sempre_descricao"
                      />
                      <span></span>
                    </label>
                  </span>
                </b-col>
              </div>
                </div>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Impressões">
            <b-card-footer class="config-tab-body">
              <section class="printing-card qz-global-card" data-config-key="qz-tray">
                <div class="qz-card-header">
                  <h5 class="printing-card-title">QZ Tray e impressoras</h5>
                  <p class="qz-connection-status" role="status" :class="{ 'qz-connection-status-connected': restaurante_qz_verificado }">
                    {{ restaurante_qz_status }}
                  </p>
                </div>
              <div class="qz-setup">
                <div class="qz-steps mb-4" aria-label="Etapas da configuração do QZ Tray">
                  <button
                    v-for="passo in 3"
                    :key="passo"
                    type="button"
                    class="qz-step"
                    :class="{
                      'qz-step-current': restaurante_qz_passo === passo,
                      'qz-step-done': restaurante_qz_verificado && restaurante_qz_passo > passo
                    }"
                    :aria-current="restaurante_qz_passo === passo ? 'step' : null"
                    @click="selecionarPassoQz(passo)"
                  >
                    <span class="qz-step-number">
                      {{ restaurante_qz_verificado && restaurante_qz_passo > passo ? "✓" : passo }}
                    </span>
                    <span>{{ ["Instalação QZ Tray", "Configurar certificado", "Selecionar impressoras"][passo - 1] }}</span>
                  </button>
                </div>

                <div v-if="restaurante_qz_passo === 1" class="qz-step-content">
                  <h6 class="font-weight-bolder text-dark">Instalação</h6>
                  <div class="ml-5 mt-5 mb-5">
                    <p>
                    <strong>1 - </strong>Realizar download do <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer">QZ Tray</a>
                    </p>
                    <p>
                      <strong>2 - </strong>Executar o instalador e finalizar a instalação.
                    </p>
                    <p>
                      <strong>3 - </strong>Realizar download do <a href="#" @click.prevent="baixarCertificadoQz">Certificado Nuts</a>
                    </p>
                    <p>
                      <strong>4 - </strong>Após finalizar, clique em próximo.
                    </p>
                  </div>

                  <div class="d-flex justify-content-start mt-3">
                    <button type="button" class="btn btn-primary" @click="selecionarPassoQz(2)">
                      Próximo →
                    </button>
                  </div>
                </div>

                <div v-else-if="restaurante_qz_passo === 2" class="qz-step-content">
                  <template v-if="restaurante_qz_sistema === 'Windows'">
                    <div class="qz-guide-grid mb-3">
                      <a
                        v-for="(etapa, indice) in restauranteGuiaQzWindows"
                        :key="etapa.titulo"
                        :href="etapa.imagem"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="qz-guide-card"
                        :aria-label="'Ampliar imagem: ' + etapa.titulo"
                      >
                        <div class="qz-guide-caption">
                          <span class="qz-guide-number">{{ indice + 1 }}</span>
                          <span>
                            <strong>{{ etapa.titulo }}</strong>
                          </span>
                        </div>
                        <span class="qz-guide-image">
                          <img :src="etapa.imagem" :alt="etapa.alt" />
                        </span>
                      </a>
                    </div>
                  </template>

                  <div class="d-flex flex-wrap justify-content-between qz-guide-actions">
                    <button type="button" class="btn btn-light mb-2" @click="selecionarPassoQz(1)">
                      ← Voltar
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary mb-2"
                      :disabled="restaurante_qz_carregando"
                      @click="verificarQzRestaurante"
                    >
                      {{ restaurante_qz_carregando ? "Verificando..." : "Testar conexão e prosseguir" }}
                    </button>
                  </div>
                </div>

                <div v-else class="qz-step-content">
                  <h6 class="font-weight-bolder text-dark">Selecione as impressoras</h6>
                  <p class="text-muted mb-3">
                    Os nomes escolhidos precisam existir no computador que executa o QZ Tray.
                  </p>
                  <div class="qz-printers-grid">
                    <div v-for="destino in restauranteDestinosImpressao" :key="destino.id" class="qz-printer-item">
                      <b-form-group :label="destino.label" :label-for="'qz-' + destino.id">
                        <b-form-select
                          :id="'qz-' + destino.id"
                          :value="restauranteImpressoraDestino(destino.id)"
                          :title="restauranteImpressoraDestino(destino.id) || ''"
                          :options="restauranteImpressorasOptions"
                          @input="alterarImpressoraDestino(destino.id, $event)"
                        />
                      </b-form-group>
                    </div>
                  </div>
                  <div class="qz-printer-actions">
                    <button type="button" class="btn btn-light" @click="selecionarPassoQz(2)">
                      ← Voltar
                    </button>
                    <div class="qz-printer-action-main">
                      <button
                        type="button"
                        class="btn btn-light-success font-weight-bold"
                        :disabled="restaurante_qz_carregando || restaurante_qz_testando"
                        @click="testarImpressaoRestaurante"
                      >
                        {{ restaurante_qz_testando ? "Testando..." : "Testar impressão" }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-light-primary font-weight-bold"
                        :disabled="restaurante_qz_carregando"
                        @click="carregarImpressorasRestaurante"
                      >
                        {{ restaurante_qz_carregando ? "Conectando..." : "Atualizar impressoras" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </section>
              <div class="printing-grid">
                <section class="printing-card">
                  <h5 class="printing-card-title">PDV / Pré-venda</h5>
                  <div class="config-row" data-config-key="retratoImpressao">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão retrato na Pré-venda
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="retratoImpressao" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="viasPrevenda">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        2 Vias na Pré-venda
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="viasPrevenda" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="ordenacao_produtos_prevenda">
                    <b-col md="5">
                      <p class="font-weight-bolder text-dark mb-2">
                        Ordenar Impressão da Pré-Venda pela descrição dos produtos
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="ordenacao_produtos_prevenda"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="nao_gerar_impressao">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Não gerar impressão de Pré-vendas/NFCe
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="nao_gerar_impressao" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                </section>
                <section class="printing-card">
                  <h5 class="printing-card-title">NFC-e</h5>
                  <div class="config-row" data-config-key="margemNFCe">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Margem de 4mm na NFC-e
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="margemNFCe" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="imprimir_nfce_a4">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Imprimir NFC-e em A4
                      </p>
                      <span class="switch switch-icon">
                        <label
                          v-b-tooltip.hover.right="
                            'Quando ativo, a NFC-e será gerada somente em folha A4.'
                          "
                        >
                          <input type="checkbox" v-model="imprimir_nfce_a4" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="duplicata_nfce">
                    <b-row>
                      <b-col md="3">
                        <p class="font-weight-bolder text-dark mb-2">
                          Impressão duplicata para NFCe
                        </p>
                        <span class="switch switch-icon">
                          <label>
                            <input type="checkbox" v-model="duplicata_nfce" />
                            <span></span>
                          </label>
                        </span>
                      </b-col>
                      <b-col md="3" v-if="duplicata_nfce" class="config-dependent-fields">
                        <p class="font-weight-bolder text-dark mb-2">
                          Imprimir em 80 mm
                        </p>
                        <span class="switch switch-icon">
                          <label>
                            <input
                              type="checkbox"
                              v-model="impressao_duplicata_nfce_80_colunas"
                            />
                            <span></span>
                          </label>
                        </span>
                      </b-col>
                    </b-row>
                  </div>
                </section>
                <section class="printing-card">
                  <h5 class="printing-card-title">NFe / DANFE</h5>
                  <div class="config-row" data-config-key="referencia_danfe">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Utilizar referência na gereção da Danfe
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="referencia_danfe" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_paisagem_danfe">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão paisagem Danfe
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_paisagem_danfe"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                </section>
                <section class="printing-card">
                  <h5 class="printing-card-title">Recibos / Gerais de impressão</h5>
                  <div class="config-row" data-config-key="impressao_recibo_80_colunas">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão recibo 80 colunas
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_recibo_80_colunas"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                </section>
                <section class="printing-card">
                  <h5 class="printing-card-title">Pedidos</h5>
                  <div class="config-row" data-config-key="impressao_pedido_80_colunas">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão pedidos em 80 colunas
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_pedido_80_colunas"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_pedido_faturado_80_colunas">
                    <b-col md="4">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão de pedido faturado em 80 colunas
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_pedido_faturado_80_colunas"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_emitente_documentos">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Não imprimir dados do emitente
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_emitente_documentos"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="pedido_reduzido">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão reduzida pedido
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="pedido_reduzido" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_ncm_pedido">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">Impressão do NCM</p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="impressao_ncm_pedido" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="nao_imprimir_referencia_pedido">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Não imprimir referência dos produtos no Pedido
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="nao_imprimir_referencia_pedido" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                </section>
                <section class="printing-card">
                  <h5 class="printing-card-title">Orçamentos</h5>
                  <div class="config-row" data-config-key="imprimir_codigo_barra_orcamento">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão código de barras nos Orçamentos
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="imprimir_codigo_barra_orcamento"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_orcamento_80_colunas">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão do orçamento em 80 colunas
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_orcamento_80_colunas"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_ncm_orcamento">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">Impressão do NCM</p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="impressao_ncm_orcamento" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="nao_imprimir_referencia_orcamento">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Não imprimir referência dos produtos no Orçamento
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="nao_imprimir_referencia_orcamento" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                </section>
                <section class="printing-card">
                  <h5 class="printing-card-title">OS</h5>
                  <div class="config-row" data-config-key="impressao_os_80_colunas">
                    <b-col md="3">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão OS em 80 colunas
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input
                            type="checkbox"
                            v-model="impressao_os_80_colunas"
                          />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                  <div class="config-row" data-config-key="impressao_msg_os">
                    <b-col md="6">
                      <p class="font-weight-bolder text-dark mb-2">
                        Impressão mensagem (art.1275, INCISO III DO CCB) na OS
                      </p>
                      <span class="switch switch-icon">
                        <label>
                          <input type="checkbox" v-model="impressao_msg_os" />
                          <span></span>
                        </label>
                      </span>
                    </b-col>
                  </div>
                </section>
              </div>
            </b-card-footer>
          </b-tab>
          <b-tab no-body title="Balanças" lazy>
            <b-card-footer class="config-tab-body">
              <BalancaConfiguracao />
            </b-card-footer>
          </b-tab>
        </b-tabs>
            </div>
          </div>
          <b-modal
        v-model="show_observacao_fixa_modal"
        :title="observacaoFixaModalTitle"
        hide-footer
      >
        <b-form-group label="Mensagem" label-for="observacao_fixa_mensagem">
          <b-form-textarea
            id="observacao_fixa_mensagem"
            v-model="observacao_fixa_modal_mensagem"
            :maxlength="observacao_fixa_limite"
            rows="4"
            no-resize
            @input="limitarObservacaoFixa"
          />
          <div class="d-flex justify-content-between mt-2">
            <small class="text-muted">
              Esta mensagem será enviada para impressão no PDF selecionado.
            </small>
            <small
              :class="
                observacaoFixaCaracteresRestantes < 0
                  ? 'text-danger'
                  : 'text-muted'
              "
            >
              {{ observacao_fixa_modal_mensagem.length }}/{{
                observacao_fixa_limite
              }}
            </small>
          </div>
        </b-form-group>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-light font-weight-bolder mr-2"
            @click="show_observacao_fixa_modal = false"
          >
            Cancelar
          </button>
          <button
            class="btn btn-primary font-weight-bolder"
            @click="salvarMensagemModal"
          >
            Salvar mensagem
          </button>
        </div>
          </b-modal>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { SistemaMixin } from "@/modules/Application";
import { Money } from "v-money";
import { MONEY as settingMoney } from "@/modules/Application";
import { userAccessStore } from "@/userAccessStore";
import { clienteInadimplenciaStore } from "@/modules/Application/stores/ClienteInadimplenciaStore";
import { FEATURES } from "@/core/constants/features";
import BalancaConfiguracao from "@/modules/Balanca/pages/Index.vue";
import BalancaEtiquetaService from "@/modules/PdvDesktop/services/BalancaEtiquetaService";
import {
  imprimirTextoQz,
  listarImpressorasQz,
  reconectarQz,
  ticketTesteProducao,
} from "@/modules/Restaurante/services/qzTrayPrinter";
import _ from "lodash";

const CONFIG_SEARCH_INDEX = [
  { key: "descontar_icms_base_piscofins", label: "Considerar desconto de ICMS na base do PIS/COFINS", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "vendas_cartao_conta_receber", label: "Vendas na NFCe no cartão devem ir para Contas a Receber?", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "abertura_caixa", label: "Abertura de caixa", section: "nfe-nfce", sectionName: "NFe/NFCe", keywords: "caixa abertura fechamento" },
  { key: "calcular_st_desconto_icms", label: "Calcular ST considerando desconto do ICMS", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "importar_obs_pessoa", label: "Importar OBS do cadastro do cliente para NFCe/Pré-Venda", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "desconto_incondicional", label: "Desconto Incondicional", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "ordenar_item_adicionado_por_ultimo_pdv", label: "Ordenar item adicionado por último no PDV", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "somar_frete_despesas_baseicms", label: "Somar Frete/Seguro/Outras/IPI despesas na Base ICMS", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "integracao_pos", label: "Integração com POS", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "fixar_natureza_nfe", label: "Fixar natureza padrão na NFe", section: "nfe-nfce", sectionName: "NFe/NFCe", keywords: "natureza operação nota fiscal" },
  { key: "nao_converter_cfop", label: "Não converter CFOP", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "vendedor_obrigatorio", label: "Obrigatório informar vendedor NFCe/Pré-Venda", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "importar_obs_produto", label: "Importar OBS do cadastro do produto para NFe", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "nao_movimentar_estoque", label: "Não movimentar estoque na Pré-venda", section: "nfe-nfce", sectionName: "NFe/NFCe", keywords: "pre venda estoque" },
  { key: "lucro_pis_cofins", label: "Gerar valores de PIS/COFINS com base no lucro da venda", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "padrao_reforma_tributaria", label: "Utilizar padrão CBS/IBS - Reforma Tributária", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "usar_codigo_balanca", label: "Utilizar código da balança", section: "nfe-nfce", sectionName: "NFe/NFCe" },
  { key: "ultimo_nsu", label: "Último NSU - Monitor NFe", section: "compras", sectionName: "Compras" },
  { key: "gerar_conta_pagar", label: "Todas as compras devem gerar as parcelas em Financeiro/Contas a pagar", section: "compras", sectionName: "Compras" },
  { key: "considerar_qtd_tributavel", label: "Considerar quantidade tributável na importação do XML", section: "compras", sectionName: "Compras" },
  { key: "importar_desc_complementar", label: "Importar informações adicionais do item de forma automática", section: "compras", sectionName: "Compras" },
  { key: "considerar_data_entrada", label: "Considerar a data de entrada na criação das contas a pagar", section: "compras", sectionName: "Compras" },
  { key: "impressao_cliente_pedido_producao", label: "Não imprimir dados do cliente para produção", section: "pedidos", sectionName: "Pedidos" },
  { key: "considerar_pedidos_faturados", label: "Considerar Pedidos faturados no Dashboard", section: "pedidos", sectionName: "Pedidos" },
  { key: "considerar_obs_produto_pedido", label: "Considerar OBS do produto no Pedido", section: "pedidos", sectionName: "Pedidos" },
  { key: "mensagem_adicional_pedido", label: "Incluir mensagem adicional no Pedido", section: "pedidos", sectionName: "Pedidos" },
  { key: "considerar_obs_produto_orcamento", label: "Considerar OBS do produto no Orçamento", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "mensagem_adicional_orcamento", label: "Incluir mensagem adicional no Orçamento", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "permitir_servicos_orcamento", label: "Permitir serviços no Orçamento", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "orcamento_com_foto", label: "Orçamento com foto", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "apresentar_objeto_identificador_orcamento", label: "Apresentar objeto/identificador no orçamento", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "fixar_observacao_orcamento", label: "Fixar observações nos PDFs", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "custo_operacional", label: "Custo Operacional", section: "orcamentos", sectionName: "Orçamentos" },
  { key: "fixar_observacao_os", label: "Fixar observações nos PDFs", section: "os", sectionName: "OS" },
  { key: "trabalhar_horas_os", label: "Utilizar controle de horas no lançamento da OS", section: "os", sectionName: "OS" },
  { key: "trabalhar_numero_interno_os", label: "Utilizar número interno na OS", section: "os", sectionName: "OS" },
  { key: "considerar_os_faturadas_nfse_feitas", label: "Considerar OS faturadas e NFSe feitas no Dashboard", section: "os", sectionName: "OS" },
  { key: "informacao_nfse", label: "Informação complementar fixa", section: "nfse", sectionName: "NFSe" },
  { key: "restaurante-canais", label: "Canais digitais do restaurante", section: "restaurante", sectionName: "Restaurante", keywords: "cardápio digital self pedido" },
  { key: "restaurante_producao_etapa_unica", label: "Etapa única na produção do restaurante", section: "restaurante", sectionName: "Restaurante" },
  { key: "restaurante_usar_apenas_comandas", label: "Usar apenas comandas", section: "restaurante", sectionName: "Restaurante" },
  { key: "restaurante_impressao_qz_tray", label: "Habilitar impressão automática dos pedidos", section: "restaurante", sectionName: "Restaurante" },
  { key: "qz-tray", label: "QZ Tray e impressoras", section: "impressoes", sectionName: "Impressões", keywords: "impressora pdv checkout comprovante cozinha bar delivery" },
  { key: "verifica_cadastro_pessoa", label: "Verificar se a pessoa já está cadastrada", section: "gerais", sectionName: "Gerais" },
  { key: "juros_simples", label: "Calcular Juros Simples", section: "gerais", sectionName: "Gerais" },
  { key: "taxa_diaria", label: "Taxa mensal e multa", section: "gerais", sectionName: "Gerais", keywords: "juros multa", visibleWhen: "juros_simples" },
  { key: "contas_marcadas_color", label: "Cor Contas marcadas", section: "gerais", sectionName: "Gerais" },
  { key: "casas_decimal_preco", label: "Casas decimais preço", section: "gerais", sectionName: "Gerais" },
  { key: "casas_decimal_quantidade", label: "Casas decimais quantidade", section: "gerais", sectionName: "Gerais" },
  { key: "solicita_senha_adm", label: "Solicitar senha de Administrador", section: "gerais", sectionName: "Gerais" },
  { key: "checar_codigo_barras_duplicado", label: "Checar código de barras duplicado", section: "gerais", sectionName: "Gerais" },
  { key: "utilizar_sempre_referencia", label: "Utilizar sempre o campo referência para pesquisar os produtos", section: "gerais", sectionName: "Gerais" },
  { key: "sync_vendizap", label: "Sincronizar os dados de cadastros com VendiZap", section: "gerais", sectionName: "Gerais" },
  { key: "relatorios_html", label: "Definir formato (.html) como padrão dos relatórios", section: "gerais", sectionName: "Gerais" },
  { key: "verifica_estoque", label: "Verificar estoque nas vendas", section: "estoque", sectionName: "Estoque" },
  { key: "desconsiderar_estoque_condicional", label: "Desconsiderar estoque em condicionais", section: "estoque", sectionName: "Estoque" },
  { key: "bloquear_venda_cliente_inadimplente", label: "Bloquear venda para cliente com inadimplência", section: "gerais", sectionName: "Gerais" },
  { key: "utilizar_sempre_descricao", label: "Utilizar sempre o campo descrição para pesquisar os produtos", section: "gerais", sectionName: "Gerais" },
  { key: "retratoImpressao", label: "Impressão retrato na Pré-venda", section: "impressoes", sectionName: "Impressões", group: "PDV / Pré-venda" },
  { key: "viasPrevenda", label: "2 Vias na Pré-venda", section: "impressoes", sectionName: "Impressões", group: "PDV / Pré-venda" },
  { key: "ordenacao_produtos_prevenda", label: "Ordenar Impressão da Pré-Venda pela descrição dos produtos", section: "impressoes", sectionName: "Impressões", group: "PDV / Pré-venda" },
  { key: "nao_gerar_impressao", label: "Não gerar impressão de Pré-vendas/NFCe", section: "impressoes", sectionName: "Impressões", group: "PDV / Pré-venda" },
  { key: "margemNFCe", label: "Margem de 4mm na NFC-e", section: "impressoes", sectionName: "Impressões", group: "NFC-e" },
  { key: "imprimir_nfce_a4", label: "Imprimir NFC-e em A4", section: "impressoes", sectionName: "Impressões", group: "NFC-e" },
  { key: "duplicata_nfce", label: "Impressão duplicata para NFCe", section: "impressoes", sectionName: "Impressões", group: "NFC-e" },
  { key: "referencia_danfe", label: "Utilizar referência na gereção da Danfe", section: "impressoes", sectionName: "Impressões", group: "NFe / DANFE" },
  { key: "impressao_paisagem_danfe", label: "Impressão paisagem Danfe", section: "impressoes", sectionName: "Impressões", group: "NFe / DANFE" },
  { key: "impressao_recibo_80_colunas", label: "Impressão recibo 80 colunas", section: "impressoes", sectionName: "Impressões", group: "Recibos / Gerais de impressão" },
  { key: "impressao_pedido_80_colunas", label: "Impressão pedidos em 80 colunas", section: "impressoes", sectionName: "Impressões", group: "Pedidos" },
  { key: "impressao_pedido_faturado_80_colunas", label: "Impressão de pedido faturado em 80 colunas", section: "impressoes", sectionName: "Impressões", group: "Pedidos" },
  { key: "impressao_emitente_documentos", label: "Não imprimir dados do emitente", section: "impressoes", sectionName: "Impressões", group: "Pedidos" },
  { key: "pedido_reduzido", label: "Impressão reduzida pedido", section: "impressoes", sectionName: "Impressões", group: "Pedidos" },
  { key: "impressao_ncm_pedido", label: "Impressão do NCM", section: "impressoes", sectionName: "Impressões", group: "Pedidos", keywords: "ncm pedido" },
  { key: "nao_imprimir_referencia_pedido", label: "Não imprimir referência dos produtos no Pedido", section: "impressoes", sectionName: "Impressões", group: "Pedidos" },
  { key: "imprimir_codigo_barra_orcamento", label: "Impressão código de barras nos Orçamentos", section: "impressoes", sectionName: "Impressões", group: "Orçamentos" },
  { key: "impressao_orcamento_80_colunas", label: "Impressão do orçamento em 80 colunas", section: "impressoes", sectionName: "Impressões", group: "Orçamentos" },
  { key: "impressao_ncm_orcamento", label: "Impressão do NCM", section: "impressoes", sectionName: "Impressões", group: "Orçamentos", keywords: "ncm orçamento" },
  { key: "nao_imprimir_referencia_orcamento", label: "Não imprimir referência dos produtos no Orçamento", section: "impressoes", sectionName: "Impressões", group: "Orçamentos" },
  { key: "impressao_os_80_colunas", label: "Impressão OS em 80 colunas", section: "impressoes", sectionName: "Impressões", group: "OS" },
  { key: "impressao_msg_os", label: "Impressão mensagem (art.1275, INCISO III DO CCB) na OS", section: "impressoes", sectionName: "Impressões", group: "OS" },
  { key: "fechamento_as_cegas", label: "Fechamento às cegas", section: "nfe-nfce", sectionName: "NFe/NFCe", target: "abertura_caixa", visibleWhen: "abertura_caixa" },
  { key: "natureza_nfe", label: "Natureza padrão da NFe", section: "nfe-nfce", sectionName: "NFe/NFCe", target: "fixar_natureza_nfe", visibleWhen: "fixar_natureza_nfe" },
  { key: "balanca_tamanho_codigo_produto", label: "Dígitos do código do produto", section: "nfe-nfce", sectionName: "NFe/NFCe", target: "usar_codigo_balanca", visibleWhen: "usar_codigo_balanca" },
  { key: "balanca_etiqueta_tipo", label: "Etiqueta com peso ou preço", section: "nfe-nfce", sectionName: "NFe/NFCe", target: "usar_codigo_balanca", visibleWhen: "usar_codigo_balanca" },
  { key: "balanca_casas_decimais", label: "Casas decimais do peso (kg)", section: "nfe-nfce", sectionName: "NFe/NFCe", target: "usar_codigo_balanca", visibleWhen: (vm) => vm.usar_codigo_balanca && vm.balanca_etiqueta_tipo === "peso" },
  { key: "mensagem_adicional_texto_pedido", label: "Texto da mensagem adicional do Pedido", section: "pedidos", sectionName: "Pedidos", target: "mensagem_adicional_pedido", visibleWhen: "mensagem_adicional_pedido" },
  { key: "mensagem_adicional_texto_orcamento", label: "Texto da mensagem adicional do Orçamento", section: "orcamentos", sectionName: "Orçamentos", target: "mensagem_adicional_orcamento", visibleWhen: "mensagem_adicional_orcamento" },
  { key: "custo_percentual", label: "Custo %", section: "orcamentos", sectionName: "Orçamentos", target: "custo_operacional", visibleWhen: "custo_operacional" },
  { key: "multa", label: "Multa %", section: "gerais", sectionName: "Gerais", target: "taxa_diaria", visibleWhen: "juros_simples" },
  { key: "limite_inadimplencia_permitida", label: "Valor limite permitido", section: "gerais", sectionName: "Gerais", target: "bloquear_venda_cliente_inadimplente", visibleWhen: "bloquear_venda_cliente_inadimplente" },
  { key: "impressao_duplicata_nfce_80_colunas", label: "Imprimir duplicata em 80 mm", section: "impressoes", sectionName: "Impressões", group: "NFC-e", target: "duplicata_nfce", visibleWhen: "duplicata_nfce" },
];

const normalizarBusca = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const CAMPOS_AUTOSAVE_COM_DEBOUNCE = new Set([
  "ultimo_nsu",
  "taxa_diaria",
  "multa",
  "mensagem_adicional_texto_pedido",
  "mensagem_adicional_texto_orcamento",
  "custo_percentual",
  "informacao_nfse",
  "balanca_tamanho_codigo_produto",
  "balanca_casas_decimais",
  "limite_inadimplencia_permitida",
]);

export default {
  name: "app-configuracoes-gerais",
  data() {
    return {
      userAccessStore,
      abaConfiguracaoAtiva: 0,
      buscaConfiguracao: "",
      buscaAberta: false,
      buscaDestaqueTimer: null,
      buscaDestaqueElemento: null,
      configuracoes: null,
      autosaveReady: false,
      autosaveUltimoSalvo: null,
      autosaveEmAndamento: null,
      autosaveTimer: null,
      autosaveStatusTimer: null,
      autosaveRevisao: 0,
      autosaveEstado: "",
      autosaveMensagem: "",
      retratoImpressao: false,
      margemNFCe: false,
      imprimir_nfce_a4: false,
      solicita_senha_adm: false,
      viasPrevenda: false,
      ultimo_nsu: null,
      verifica_cadastro_pessoa: false,
      baixa_materia_prima: false,
      abertura_caixa: false,
      fechamento_as_cegas: false,
      juros_simples: false,
      configMoney: settingMoney,
      taxa_diaria: 0,
      multa: 0,
      casaDecimaisOptions: [
        { value: 2, text: "2" },
        { value: 3, text: "3" },
        { value: 4, text: "4" },
        { value: 5, text: "5" },
        { value: 6, text: "6" },
        { value: 7, text: "7" },
      ],
      descontar_icms_base_piscofins: false,
      integracao_pos: false,
      vendas_cartao_conta_receber: false,
      contas_marcadas_color: null,
      impressao_os_80_colunas: false,
      impressao_msg_os: false,
      casas_decimal_preco: 2,
      casas_decimal_quantidade: 2,
      calcular_st_desconto_icms: false,
      duplicata_nfce: false,
      impressao_duplicata_nfce_80_colunas: false,
      gerar_conta_pagar: false,
      utilizar_sempre_referencia: false,
      checar_codigo_barras_duplicado: false,
      considerar_qtd_tributavel: false,
      importar_desc_complementar: false,
      somar_frete_despesas_baseicms: false,
      sync_vendizap: false,
      considerar_obs_produto_pedido: false,
      considerar_obs_produto_orcamento: false,
      mensagem_adicional_pedido: false,
      mensagem_adicional_orcamento: false,
      mensagem_adicional_texto_pedido: null,
      mensagem_adicional_texto_orcamento: null,
      impressao_pedido_80_colunas: false,
      impressao_pedido_faturado_80_colunas: false,
      relatorios_html: false,
      importar_obs_pessoa: false,
      referencia_danfe: false,
      desconto_incondicional: false,
      ordenacao_produtos_prevenda: false,
      ordenar_item_adicionado_por_ultimo_pdv: false,
      impressao_paisagem_danfe: false,
      verifica_estoque: false,
      custo_operacional: false,
      custo_percentual: 0,
      nao_gerar_impressao: false,
      fixar_natureza_nfe: false,
      natureza_nfe: null,
      naturezas_nfe: [],
      utilizar_sempre_descricao: false,
      nao_converter_cfop: false,
      imprimir_codigo_barra_orcamento: false,
      permitir_servicos_orcamento: false,
      orcamento_com_foto: false,
      apresentar_objeto_identificador_orcamento: false,
      vendedor_obrigatorio: false,
      impressao_orcamento_80_colunas: false,
      impressao_ncm_pedido: false,
      impressao_ncm_orcamento: false,
      impressao_recibo_80_colunas: false,
      informacao_nfse: null,
      impressao_emitente_documentos: false,
      impressao_cliente_pedido_producao: false,
      trabalhar_horas_os: false,
      trabalhar_numero_interno_os: false,
      pedido_reduzido: false,
      importar_obs_produto: false,
      nao_imprimir_referencia_pedido: false,
      nao_imprimir_referencia_orcamento: false,
      nao_movimentar_estoque: false,
      lucro_pis_cofins: false,
      considerar_data_entrada: false,
      considerar_pedidos_faturados: false,
      considerar_os_faturadas_nfse_feitas: false,
      padrao_reforma_tributaria: false,
      usar_codigo_balanca: false,
      balanca_etiqueta_tipo: "peso",
      balanca_tamanho_codigo_produto: 6,
      balanca_casas_decimais: 3,
      bloquear_venda_cliente_inadimplente: false,
      limite_inadimplencia_permitida: 0,
      desconsiderar_estoque_condicional: false,
      fixar_observacao_os: false,
      fixar_observacao_orcamento: false,
      observacao_fixa_os: null,
      observacao_fixa_orcamento: null,
      observacao_fixa_limite: 255,
      observacao_fixa_modal_tipo: null,
      restaurante_producao_etapa_unica: false,
      restaurante_usar_apenas_comandas: false,
      restaurante_cardapio_online_ativo: false,
      restaurante_self_pedido_ativo: false,
      restaurante_impressao_qz_tray: false,
      impressora_pdv: null,
      restaurante_impressora_checkout: null,
      restaurante_impressora_cozinha: null,
      restaurante_impressora_bar: null,
      restaurante_impressora_comprovante_cliente: null,
      restaurante_impressora_delivery: null,
      restaurante_impressoras_qz: [],
      restaurante_qz_status: "QZ Tray ainda não verificado",
      restaurante_qz_carregando: false,
      restaurante_qz_testando: false,
      restaurante_qz_baixando_certificado: false,
      restaurante_qz_passo: 3,
      restaurante_qz_sistema: /Mac/i.test(navigator.platform)
        ? "macOS"
        : "Windows",
      restaurante_qz_verificado: false,
      observacao_fixa_modal_mensagem: "",
      show_observacao_fixa_modal: false,
    };
  },
  components: {
    BalancaConfiguracao,
    Money,
    VueSelect,
  },
  mixins: [SistemaMixin],
  computed: {
    ...mapGetters(["itsblocked", "isNuts", "isRevenda", "hasFeature"]),
    resultadosBusca() {
      const termos = normalizarBusca(this.buscaConfiguracao).split(/\s+/).filter(Boolean);
      if (!termos.length) return [];

      const secoes = new Set(this.abasConfiguracoes.map((aba) => aba.chave));
      const modulos = this.abasConfiguracoes.map((aba) => ({
        key: `modulo:${aba.chave}`,
        label: aba.titulo,
        section: aba.chave,
        sectionName: aba.titulo,
      }));
      return [...modulos, ...CONFIG_SEARCH_INDEX]
        .filter((item) =>
          secoes.has(item.section) &&
          (!item.visibleWhen ||
            (typeof item.visibleWhen === "function"
              ? item.visibleWhen(this)
              : this[item.visibleWhen]))
        )
        .filter((item) => {
          const conteudo = normalizarBusca(
            [item.label, item.sectionName, item.group, item.keywords].filter(Boolean).join(" ")
          );
          return termos.every((termo) => conteudo.includes(termo));
        })
        .slice(0, 20);
    },
    payloadConfigs() {
      return this.montarPayloadConfigs();
    },
    abasConfiguracoes() {
      const abas = [
        { chave: "nfe-nfce", titulo: "NFe/NFCe" },
        { chave: "compras", titulo: "Compras" },
        { chave: "pedidos", titulo: "Pedidos" },
        { chave: "orcamentos", titulo: "Orçamentos" },
        { chave: "os", titulo: "OS" },
      ];
      if (this.userAccessStore.access.nfse) {
        abas.push({ chave: "nfse", titulo: "NFSe" });
      }
      if (this.podeExibirRestaurante) {
        abas.push({ chave: "restaurante", titulo: "Restaurante" });
      }
      return [
        ...abas,
        { chave: "estoque", titulo: "Estoque" },
        { chave: "gerais", titulo: "Gerais" },
        { chave: "impressoes", titulo: "Impressões" },
        { chave: "balancas", titulo: "Balanças" },
      ];
    },
    abaBalancasIndex() {
      return this.abasConfiguracoes.findIndex((aba) => aba.chave === "balancas");
    },
    podeExibirRestaurante() {
      return (
        this.hasFeature(FEATURES.MOD_FOOD) || this.isRevenda || this.isNuts
      );
    },
    observacaoFixaModalTitle() {
      return {
        os: "Observação fixa da OS",
        orcamento: "Observação fixa do Orçamento",
        pedido: "Mensagem adicional do Pedido",
        orcamento_mensagem: "Mensagem adicional do Orçamento",
      }[this.observacao_fixa_modal_tipo];
    },
    observacaoFixaCaracteresRestantes() {
      return (
        this.observacao_fixa_limite - this.observacao_fixa_modal_mensagem.length
      );
    },
    restauranteImpressorasOptions() {
      const impressoras = [
        ...this.restaurante_impressoras_qz,
        this.impressora_pdv,
        this.restaurante_impressora_checkout,
        this.restaurante_impressora_cozinha,
        this.restaurante_impressora_bar,
        this.restaurante_impressora_comprovante_cliente,
        this.restaurante_impressora_delivery,
      ]
        .filter(Boolean)
        .filter(
          (impressora, index, lista) => lista.indexOf(impressora) === index
        )
        .sort();

      if (!impressoras.length) {
        return [{ value: null, text: "Nenhuma impressora carregada" }];
      }

      return [
        { value: null, text: "Selecione a impressora" },
        ...impressoras.map((impressora) => ({
          value: impressora,
          text: impressora,
        })),
      ];
    },
    restauranteDestinosImpressao() {
      return [
        { id: "pdv", label: "NFC-e, Pré-Venda e Conferência" },
        { id: "checkout", label: "Pedido / checkout" },
        { id: "comprovante", label: "Comprovante" },
        { id: "cozinha", label: "Cozinha" },
        { id: "bar", label: "Bar" },
        { id: "delivery", label: "Delivery" },
      ];
    },
    destinoTesteImpressao() {
      return this.restauranteDestinosImpressao
        .map((destino) => ({
          ...destino,
          impressora: this.restauranteImpressoraDestino(destino.id),
        }))
        .find((destino) => destino.impressora);
    },
    restauranteGuiaQzWindows() {
      return [
        {
          titulo: "Localize o ícone na bandeja do windows e clique",
          alt: "Ícone do QZ Tray destacado na bandeja do Windows",
          imagem: require("@/assets/qz/qz-windows-tray.png"),
        },
        {
          titulo: "Clique na opção Advanced",
          alt: "Menu do QZ Tray aberto a partir do ícone na bandeja",
          imagem: require("@/assets/qz/qz-windows-menu.png"),
        },
        {
          titulo: "Acesse Site Manager",
          alt: "Submenu Advanced com a opção Site Manager",
          imagem: require("@/assets/qz/qz-windows-site-manager.png"),
        },
        {
          titulo: "Adicione o certificado no ícone '+' e clique em Close",
          alt: "Tela Site Manager com o botão de adicionar certificado destacado",
          imagem: require("@/assets/qz/qz-windows-add-certificate.png"),
        },
      ];
    },
  },
  methods: {
    fecharBusca() {
      this.buscaAberta = false;
    },
    fecharBuscaAoClicarFora(event) {
      if (this.$refs.configSearch && !this.$refs.configSearch.contains(event.target)) {
        this.fecharBusca();
      }
    },
    abrirPrimeiroResultadoBusca() {
      if (this.buscaAberta && this.resultadosBusca.length) {
        this.abrirResultadoBusca(this.resultadosBusca[0]);
      }
    },
    async abrirResultadoBusca(resultado) {
      const indice = this.abasConfiguracoes.findIndex((aba) => aba.chave === resultado.section);
      if (indice < 0) return;
      await this.selecionarAbaConfiguracao(indice);
      if (this.abaConfiguracaoAtiva !== indice) return;

      this.fecharBusca();
      await this.$nextTick();
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const corpo = this.$el.querySelector(".config-content-body");
      if (!corpo) return;
      const alvo = [...corpo.querySelectorAll("[data-config-key]")].find(
        (elemento) => elemento.dataset.configKey === (resultado.target || resultado.key)
      );
      if (!alvo) {
        corpo.scrollTop = 0;
        return;
      }

      if (window.matchMedia("(max-width: 991px), (max-height: 640px)").matches) {
        alvo.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        corpo.scrollTo({
          top: corpo.scrollTop + alvo.getBoundingClientRect().top - corpo.getBoundingClientRect().top - 20,
          behavior: "smooth",
        });
      }
      clearTimeout(this.buscaDestaqueTimer);
      if (this.buscaDestaqueElemento) {
        this.buscaDestaqueElemento.classList.remove("config-search-highlight");
      }
      alvo.classList.add("config-search-highlight");
      this.buscaDestaqueElemento = alvo;
      this.buscaDestaqueTimer = setTimeout(() => {
        alvo.classList.remove("config-search-highlight");
        this.buscaDestaqueElemento = null;
      }, 1800);
    },
    normalizarObservacaoFixa(mensagem) {
      return String(mensagem ?? "").slice(0, this.observacao_fixa_limite);
    },
    campoMensagemModal(tipo) {
      return {
        os: { toggle: "fixar_observacao_os", texto: "observacao_fixa_os" },
        orcamento: { toggle: "fixar_observacao_orcamento", texto: "observacao_fixa_orcamento" },
        pedido: { toggle: "mensagem_adicional_pedido", texto: "mensagem_adicional_texto_pedido" },
        orcamento_mensagem: { toggle: "mensagem_adicional_orcamento", texto: "mensagem_adicional_texto_orcamento" },
      }[tipo];
    },
    abrirModalMensagem(tipo) {
      const campo = this.campoMensagemModal(tipo);
      if (!campo || !this[campo.toggle]) return;
      this.observacao_fixa_modal_tipo = tipo;
      this.observacao_fixa_modal_mensagem = this.normalizarObservacaoFixa(
        this[campo.texto]
      );
      this.show_observacao_fixa_modal = true;
    },
    limitarObservacaoFixa(mensagem) {
      this.observacao_fixa_modal_mensagem =
        this.normalizarObservacaoFixa(mensagem);
    },
    salvarMensagemModal() {
      const campo = this.campoMensagemModal(this.observacao_fixa_modal_tipo);
      if (!campo) return;
      const mensagem = this.normalizarObservacaoFixa(
        this.observacao_fixa_modal_mensagem
      );
      this[campo.texto] =
        this.observacao_fixa_modal_tipo === "os" ||
        this.observacao_fixa_modal_tipo === "orcamento"
          ? mensagem.trim() || null
          : mensagem;

      this.show_observacao_fixa_modal = false;
    },
    async carregarNaturezaNFe(id) {
      if (!id) {
        this.natureza_nfe = null;
        this.naturezas_nfe = [];
        return;
      }

      try {
        const { data } = await this.$http.post("/natureza/find", { id });
        const natureza = data?.dados ?? data;

        this.natureza_nfe = natureza || null;
        this.naturezas_nfe = natureza ? [natureza] : [];
      } catch (error) {
        this.natureza_nfe = null;
        this.naturezas_nfe = [];
      }
    },
    onSearchNaturezaNFe(search, loading) {
      if (search.length) {
        loading(true);
        this.searchNaturezaNFe(loading, search, this);
      }
    },
    searchNaturezaNFe: _.debounce(async (loading, search, vm) => {
      vm.naturezas_nfe = await vm.getNaturezaByDescricao(search);
      if (loading) loading(false);
    }, 350),
    async getConfigurationList() {
      const etiquetaLocal = await BalancaEtiquetaService.carregarLocal();
      this.usar_codigo_balanca = etiquetaLocal.habilitada;
      this.balanca_etiqueta_tipo = etiquetaLocal.tipo;
      this.balanca_tamanho_codigo_produto = etiquetaLocal.tamanhoCodigoProduto;
      this.balanca_casas_decimais = etiquetaLocal.casasDecimais;
      try {
        const response = await this.$http.post("/configuracoesgerais/find");
        const configuracao = Array.isArray(response.data)
          ? response.data[0] || {}
          : response.data || {};

        this.configuracoes = Array.isArray(response.data)
          ? response.data
          : [configuracao];
        this.retratoImpressao = configuracao.impressao_retrato_prevenda;
        this.checar_codigo_barras_duplicado =
          configuracao?.checar_codigo_barras_duplicado ?? false;
        this.margemNFCe = configuracao.impressao_margem_nfce;
        this.imprimir_nfce_a4 = configuracao?.imprimir_nfce_a4 ?? false;
        this.viasPrevenda = configuracao.imprimir_vias_prevenda;
        this.ultimo_nsu = configuracao.ultimo_nsu;
        this.verifica_cadastro_pessoa = configuracao.verifica_cadastro_pessoa;
        this.baixa_materia_prima = configuracao.baixa_materia_prima;
        this.juros_simples = configuracao.juros_simples;
        this.taxa_diaria = configuracao.taxa_diaria;
        this.multa = configuracao.multa;
        this.descontar_icms_base_piscofins =
          configuracao.descontar_icms_base_piscofins;
        this.integracao_pos = configuracao.integracao_pos;
        this.vendas_cartao_conta_receber =
          configuracao.vendas_cartao_conta_receber;
        this.impressao_os_80_colunas = configuracao.impressao_os_80_colunas;
        this.impressao_msg_os = configuracao.impressao_msg_os;
        this.casas_decimal_quantidade = configuracao.casas_decimal_quantidade;
        this.casas_decimal_preco = configuracao.casas_decimal_preco;
        this.solicita_senha_adm = configuracao.solicita_senha_adm;
        this.abertura_caixa = configuracao?.abertura_caixa ?? false;
        this.fechamento_as_cegas = configuracao?.fechamento_as_cegas ?? false;
        this.calcular_st_desconto_icms =
          configuracao?.calcular_st_desconto_icms ?? false;
        this.duplicata_nfce = configuracao?.duplicata_nfce ?? false;
        this.impressao_duplicata_nfce_80_colunas =
          configuracao?.impressao_duplicata_nfce_80_colunas ?? false;
        this.gerar_conta_pagar = configuracao?.gerar_conta_pagar ?? false;
        this.utilizar_sempre_referencia =
          configuracao?.utilizar_sempre_referencia ?? false;
        this.considerar_qtd_tributavel =
          configuracao?.considerar_qtd_tributavel ?? false;
        this.importar_desc_complementar =
          configuracao?.importar_desc_complementar ?? false;
        this.somar_frete_despesas_baseicms =
          configuracao?.somar_frete_despesas_baseicms ?? false;
        this.sync_vendizap = configuracao?.sync_vendizap ?? false;
        this.considerar_obs_produto_pedido =
          configuracao?.considerar_obs_produto_pedido ?? configuracao?.considerar_obs ?? false;
        this.considerar_obs_produto_orcamento =
          configuracao?.considerar_obs_produto_orcamento ?? configuracao?.considerar_obs ?? false;
        this.mensagem_adicional_pedido =
          configuracao?.mensagem_adicional_pedido ?? configuracao?.mensagem_adicional ?? false;
        this.mensagem_adicional_orcamento =
          configuracao?.mensagem_adicional_orcamento ?? configuracao?.mensagem_adicional ?? false;
        this.mensagem_adicional_texto_pedido =
          configuracao?.mensagem_adicional_texto_pedido ?? configuracao?.mensagem_adicional_texto ?? null;
        this.mensagem_adicional_texto_orcamento =
          configuracao?.mensagem_adicional_texto_orcamento ?? configuracao?.mensagem_adicional_texto ?? null;
        this.impressao_pedido_80_colunas =
          response.data[0]?.impressao_pedido_80_colunas ?? false;
        this.impressao_pedido_faturado_80_colunas =
          response.data[0]?.impressao_pedido_faturado_80_colunas ?? false;
        this.relatorios_html = response.data[0]?.relatorios_html ?? false;
        this.importar_obs_pessoa =
          response.data[0]?.importar_obs_pessoa ?? false;
        this.referencia_danfe = response.data[0]?.referencia_danfe ?? false;
        this.desconto_incondicional =
          configuracao?.desconto_incondicional ?? false;
        this.ordenacao_produtos_prevenda =
          response.data[0]?.ordenacao_produtos_prevenda ?? false;
        this.ordenar_item_adicionado_por_ultimo_pdv =
          response.data[0]?.ordenar_item_adicionado_por_ultimo_pdv ?? false;
        this.impressao_paisagem_danfe =
          response.data[0]?.impressao_paisagem_danfe ?? false;
        this.verifica_estoque = response.data[0]?.verifica_estoque ?? false;
        this.custo_operacional = response.data[0]?.custo_operacional ?? false;
        this.custo_percentual = response.data[0]?.custo_percentual ?? false;
        this.nao_gerar_impressao =
          response.data[0]?.nao_gerar_impressao ?? false;
        this.fixar_natureza_nfe = response.data[0]?.fixar_natureza_nfe ?? false;
        await this.carregarNaturezaNFe(response.data[0]?.id_natureza_nfe);
        this.utilizar_sempre_descricao =
          configuracao?.utilizar_sempre_descricao ?? false;
        this.nao_converter_cfop = configuracao?.nao_converter_cfop ?? false;
        this.imprimir_codigo_barra_orcamento =
          configuracao?.imprimir_codigo_barra_orcamento ?? false;
        this.permitir_servicos_orcamento =
          configuracao?.permitir_servicos_orcamento ?? false;
        this.orcamento_com_foto = configuracao?.orcamento_com_foto ?? false;
        this.apresentar_objeto_identificador_orcamento =
          configuracao?.apresentar_objeto_identificador_orcamento ?? false;
        this.vendedor_obrigatorio = configuracao?.vendedor_obrigatorio ?? false;
        this.impressao_orcamento_80_colunas =
          configuracao?.impressao_orcamento_80_colunas ?? false;
        this.impressao_ncm_pedido =
          configuracao?.impressao_ncm_pedido ?? configuracao?.impressao_ncm ?? false;
        this.impressao_ncm_orcamento =
          configuracao?.impressao_ncm_orcamento ?? configuracao?.impressao_ncm ?? false;
        this.impressao_recibo_80_colunas =
          configuracao?.impressao_recibo_80_colunas ?? false;
        this.informacao_nfse = configuracao?.informacao_nfse ?? null;
        this.impressao_emitente_documentos =
          configuracao?.impressao_emitente_documentos ?? null;
        this.impressao_cliente_pedido_producao =
          !!configuracao?.impressao_cliente_pedido_producao;
        this.trabalhar_horas_os = configuracao?.trabalhar_horas_os ?? false;
        this.trabalhar_numero_interno_os =
          configuracao?.trabalhar_numero_interno_os ?? false;
        this.pedido_reduzido = configuracao?.pedido_reduzido ?? false;
        this.importar_obs_produto = configuracao?.importar_obs_produto ?? false;
        this.nao_imprimir_referencia_pedido =
          configuracao?.nao_imprimir_referencia_pedido ?? configuracao?.nao_imprimir_referencia ?? false;
        this.nao_imprimir_referencia_orcamento =
          configuracao?.nao_imprimir_referencia_orcamento ?? configuracao?.nao_imprimir_referencia ?? false;
        this.nao_movimentar_estoque =
          configuracao?.nao_movimentar_estoque ?? false;
        this.lucro_pis_cofins = configuracao?.lucro_pis_cofins ?? false;
        this.considerar_data_entrada =
          response.data[0]?.considerar_data_entrada ?? false;
        this.considerar_pedidos_faturados =
          response.data[0]?.considerar_pedidos_faturados ?? false;
        this.considerar_os_faturadas_nfse_feitas =
          response.data[0]?.considerar_os_faturadas_nfse_feitas ?? false;
        this.padrao_reforma_tributaria =
          response.data[0]?.padrao_reforma_tributaria ?? false;
        const etiqueta = BalancaEtiquetaService.mesclar(etiquetaLocal, configuracao);
        this.usar_codigo_balanca = etiqueta.habilitada;
        this.balanca_etiqueta_tipo = etiqueta.tipo;
        this.balanca_tamanho_codigo_produto = etiqueta.tamanhoCodigoProduto;
        this.balanca_casas_decimais = etiqueta.casasDecimais;
        this.bloquear_venda_cliente_inadimplente =
          configuracao?.bloquear_venda_cliente_inadimplente ?? false;
        this.limite_inadimplencia_permitida =
          configuracao?.limite_inadimplencia_permitida ?? 0;
        this.desconsiderar_estoque_condicional =
          configuracao?.desconsiderar_estoque_condicional ?? false;
        this.observacao_fixa_os =
          this.normalizarObservacaoFixa(configuracao?.observacao_fixa_os) ||
          null;
        this.observacao_fixa_orcamento =
          this.normalizarObservacaoFixa(
            configuracao?.observacao_fixa_orcamento
          ) || null;
        this.fixar_observacao_os =
          configuracao?.fixar_observacao_os ?? !!this.observacao_fixa_os;
        this.fixar_observacao_orcamento =
          configuracao?.fixar_observacao_orcamento ??
          !!this.observacao_fixa_orcamento;
        this.restaurante_producao_etapa_unica =
          configuracao?.restaurante_producao_etapa_unica ?? false;
        this.restaurante_usar_apenas_comandas =
          configuracao?.restaurante_usar_apenas_comandas ?? false;
        this.restaurante_cardapio_online_ativo =
          configuracao?.restaurante_cardapio_online_ativo ?? false;
        this.restaurante_self_pedido_ativo =
          configuracao?.restaurante_self_pedido_ativo ?? false;
        const impressaoQzSalva =
          configuracao?.restaurante_impressao_qz_tray ?? false;
        this.restaurante_impressao_qz_tray =
          impressaoQzSalva === true ||
          impressaoQzSalva === 1 ||
          impressaoQzSalva === "1" ||
          impressaoQzSalva === "true";
        this.restaurante_impressora_checkout =
          configuracao?.restaurante_impressora_checkout ?? null;
        this.impressora_pdv = configuracao?.impressora_pdv ?? null;
        this.restaurante_impressora_cozinha =
          configuracao?.restaurante_impressora_cozinha ?? null;
        this.restaurante_impressora_bar =
          configuracao?.restaurante_impressora_bar ?? null;
        this.restaurante_impressora_comprovante_cliente =
          configuracao?.restaurante_impressora_comprovante_cliente ?? null;
        this.restaurante_impressora_delivery =
          configuracao?.restaurante_impressora_delivery ?? null;
        await this.$nextTick();
        this.autosaveUltimoSalvo = _.cloneDeep(this.payloadConfigs);
        this.autosaveReady = true;
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            "Não foi possível carregar as configurações gerais."
        );
      }
    },
    montarPayloadConfigs() {
      const tamanhoProduto = Number(this.balanca_tamanho_codigo_produto);
      const casasDecimais = Number(this.balanca_casas_decimais);
      return {
          impressao_retrato_prevenda: this.retratoImpressao ?? false,
          checar_codigo_barras_duplicado: this.checar_codigo_barras_duplicado ?? false,
          impressao_margem_nfce: this.margemNFCe ?? false,
          imprimir_nfce_a4: this.imprimir_nfce_a4 ?? false,
          imprimir_vias_prevenda: this.viasPrevenda ?? false,
          ultimo_nsu: this.ultimo_nsu ?? null,
          verifica_cadastro_pessoa: this.verifica_cadastro_pessoa ?? null,
          baixa_materia_prima: this.baixa_materia_prima ?? null,
          juros_simples: this.juros_simples ?? null,
          taxa_diaria: this.taxa_diaria ?? null,
          multa: this.multa ?? null,
          descontar_icms_base_piscofins:
            this.descontar_icms_base_piscofins ?? null,
          integracao_pos: this.integracao_pos ?? null,
          vendas_cartao_conta_receber: this.vendas_cartao_conta_receber ?? null,
          impressao_os_80_colunas: this.impressao_os_80_colunas ?? null,
          casas_decimal_preco: this.casas_decimal_preco ?? null,
          casas_decimal_quantidade: this.casas_decimal_quantidade ?? null,
          impressao_msg_os: this.impressao_msg_os ?? null,
          solicita_senha_adm: this.solicita_senha_adm ?? null,
          abertura_caixa: this.abertura_caixa,
          fechamento_as_cegas: this.abertura_caixa
            ? this.fechamento_as_cegas
            : false,
          calcular_st_desconto_icms: this.calcular_st_desconto_icms ?? null,
          duplicata_nfce: this.duplicata_nfce ?? null,
          impressao_duplicata_nfce_80_colunas: this.duplicata_nfce
            ? this.impressao_duplicata_nfce_80_colunas ?? false
            : false,
          gerar_conta_pagar: this.gerar_conta_pagar ?? null,
          utilizar_sempre_referencia: this.utilizar_sempre_referencia ?? null,
          considerar_qtd_tributavel: this.considerar_qtd_tributavel ?? null,
          importar_desc_complementar: this.importar_desc_complementar ?? false,
          somar_frete_despesas_baseicms:
            this.somar_frete_despesas_baseicms ?? false,
          sync_vendizap: this.sync_vendizap ?? false,
          considerar_obs_produto_pedido: this.considerar_obs_produto_pedido ?? false,
          considerar_obs_produto_orcamento: this.considerar_obs_produto_orcamento ?? false,
          mensagem_adicional_pedido: this.mensagem_adicional_pedido ?? false,
          mensagem_adicional_orcamento: this.mensagem_adicional_orcamento ?? false,
          mensagem_adicional_texto_pedido: this.mensagem_adicional_texto_pedido ?? null,
          mensagem_adicional_texto_orcamento: this.mensagem_adicional_texto_orcamento ?? null,
          impressao_pedido_80_colunas:
            this.impressao_pedido_80_colunas ?? false,
          impressao_pedido_faturado_80_colunas:
            this.impressao_pedido_faturado_80_colunas ?? false,
          relatorios_html: this.relatorios_html ?? false,
          importar_obs_pessoa: this.importar_obs_pessoa ?? false,
          referencia_danfe: this.referencia_danfe ?? false,
          desconto_incondicional: this.desconto_incondicional ?? false,
          ordenacao_produtos_prevenda:
            this.ordenacao_produtos_prevenda ?? false,
          ordenar_item_adicionado_por_ultimo_pdv:
            this.ordenar_item_adicionado_por_ultimo_pdv ?? false,
          impressao_paisagem_danfe: this.impressao_paisagem_danfe ?? false,
          verifica_estoque: this.verifica_estoque ?? false,
          custo_operacional: this.custo_operacional ?? false,
          custo_percentual: this.custo_percentual ?? false,
          nao_gerar_impressao: this.nao_gerar_impressao ?? false,
          fixar_natureza_nfe: this.fixar_natureza_nfe ?? false,
          id_natureza_nfe: this.fixar_natureza_nfe
            ? this.natureza_nfe?.id
            : null,
          utilizar_sempre_descricao: this.utilizar_sempre_descricao ?? false,
          nao_converter_cfop: this.nao_converter_cfop ?? false,
          imprimir_codigo_barra_orcamento:
            this.imprimir_codigo_barra_orcamento ?? false,
          permitir_servicos_orcamento: this.permitir_servicos_orcamento ?? false,
          orcamento_com_foto: this.orcamento_com_foto ?? false,
          apresentar_objeto_identificador_orcamento:
            this.apresentar_objeto_identificador_orcamento ?? false,
          vendedor_obrigatorio: this.vendedor_obrigatorio ?? false,
          impressao_orcamento_80_colunas:
            this.impressao_orcamento_80_colunas ?? false,
          impressao_ncm_pedido: this.impressao_ncm_pedido ?? false,
          impressao_ncm_orcamento: this.impressao_ncm_orcamento ?? false,
          impressao_recibo_80_colunas:
            this.impressao_recibo_80_colunas ?? false,
          informacao_nfse: this.informacao_nfse ?? false,
          impressao_emitente_documentos:
            this.impressao_emitente_documentos ?? false,
          impressao_cliente_pedido_producao:
            !!this.impressao_cliente_pedido_producao,
          trabalhar_horas_os: this.trabalhar_horas_os ?? false,
          trabalhar_numero_interno_os:
            this.trabalhar_numero_interno_os ?? false,
          pedido_reduzido: this.pedido_reduzido ?? false,
          importar_obs_produto: this.importar_obs_produto ?? false,
          nao_imprimir_referencia_pedido: this.nao_imprimir_referencia_pedido ?? false,
          nao_imprimir_referencia_orcamento: this.nao_imprimir_referencia_orcamento ?? false,
          nao_movimentar_estoque: this.nao_movimentar_estoque ?? false,
          lucro_pis_cofins: this.lucro_pis_cofins ?? false,
          considerar_data_entrada: this.considerar_data_entrada ?? false,
          considerar_pedidos_faturados:
            this.considerar_pedidos_faturados ?? false,
          considerar_os_faturadas_nfse_feitas:
            this.considerar_os_faturadas_nfse_feitas ?? false,
          padrao_reforma_tributaria: this.padrao_reforma_tributaria ?? false,
          usar_codigo_balanca: this.usar_codigo_balanca ?? false,
          balanca_etiqueta_tipo: this.balanca_etiqueta_tipo,
          balanca_tamanho_codigo_produto: tamanhoProduto,
          balanca_casas_decimais: casasDecimais,
          bloquear_venda_cliente_inadimplente:
            this.bloquear_venda_cliente_inadimplente ?? false,
          limite_inadimplencia_permitida: this
            .bloquear_venda_cliente_inadimplente
            ? this.limite_inadimplencia_permitida ?? 0
            : 0,
          desconsiderar_estoque_condicional:
            this.desconsiderar_estoque_condicional ?? false,
          fixar_observacao_os: this.fixar_observacao_os ?? false,
          fixar_observacao_orcamento: this.fixar_observacao_orcamento ?? false,
          observacao_fixa_os:
            this.normalizarObservacaoFixa(this.observacao_fixa_os) || null,
          observacao_fixa_orcamento:
            this.normalizarObservacaoFixa(this.observacao_fixa_orcamento) || null,
          restaurante_producao_etapa_unica:
            this.restaurante_producao_etapa_unica ?? false,
          restaurante_usar_apenas_comandas:
            this.restaurante_usar_apenas_comandas ?? false,
          restaurante_impressao_qz_tray:
            this.restaurante_impressao_qz_tray ?? false,
          impressora_pdv: this.impressora_pdv,
          restaurante_impressora_checkout: this.restaurante_impressora_checkout,
          restaurante_impressora_cozinha: this.restaurante_impressora_cozinha,
          restaurante_impressora_bar: this.restaurante_impressora_bar,
          restaurante_impressora_comprovante_cliente: this.restaurante_impressora_comprovante_cliente,
          restaurante_impressora_delivery: this.restaurante_impressora_delivery,
      };
    },
    async salvarConfigs(payload) {
        const tamanhoProduto = payload.balanca_tamanho_codigo_produto;
        const casasDecimais = payload.balanca_casas_decimais;
        if (
          payload.usar_codigo_balanca &&
          (!Number.isInteger(tamanhoProduto) || tamanhoProduto < 1 || tamanhoProduto > 10 ||
            (payload.balanca_etiqueta_tipo === "peso" &&
              (!Number.isInteger(casasDecimais) || casasDecimais < 0 || casasDecimais > 5)))
        ) {
          throw new Error("Informe um tamanho de código entre 1 e 10 e casas decimais entre 0 e 5.");
        }

        if (
          payload.bloquear_venda_cliente_inadimplente &&
          Number(payload.limite_inadimplencia_permitida) < 0
        ) {
          throw new Error("Informe um valor limite de inadimplência válido.");
        }

        if (
          payload.restaurante_impressao_qz_tray &&
          (!payload.restaurante_impressora_cozinha ||
            !payload.restaurante_impressora_bar)
        ) {
          this.$toast.error(
            "Informe as impressoras da cozinha e do bar para ativar a impressão do restaurante."
          );
        }

        if (payload.fixar_natureza_nfe && !payload.id_natureza_nfe) {
          throw new Error("Selecione uma natureza padrão para a NFe.");
        }

        const { data: configuracaoSalva } = await this.$http.post("/configuracoesgerais/create", payload);
        if (configuracaoSalva?.status === false) {
          throw new Error("O servidor não confirmou o salvamento das configurações gerais.");
        }
        await BalancaEtiquetaService.salvarLocal({
          habilitada: payload.usar_codigo_balanca,
          tipo: payload.balanca_etiqueta_tipo,
          tamanhoCodigoProduto: tamanhoProduto,
          casasDecimais,
        });
        clienteInadimplenciaStore.bloquearVendaClienteInadimplente =
          payload.bloquear_venda_cliente_inadimplente ?? false;
        clienteInadimplenciaStore.limiteInadimplenciaPermitida = payload
          .bloquear_venda_cliente_inadimplente
          ? payload.limite_inadimplencia_permitida ?? 0
          : 0;
    },
    agendarAutosave(espera = 0) {
      clearTimeout(this.autosaveTimer);
      clearTimeout(this.autosaveStatusTimer);
      this.autosaveTimer = null;
      if (_.isEqual(this.payloadConfigs, this.autosaveUltimoSalvo)) {
        if (!this.autosaveEmAndamento) this.autosaveEstado = "";
        return;
      }
      this.autosaveEstado = "pendente";
      this.autosaveMensagem = "Alterações pendentes";
      this.autosaveTimer = setTimeout(() => {
        this.autosaveTimer = null;
        this.executarAutosave();
      }, espera);
    },
    async executarAutosave() {
      if (!this.autosaveReady) return true;
      if (this.autosaveEmAndamento) return this.autosaveEmAndamento;
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
      if (_.isEqual(this.payloadConfigs, this.autosaveUltimoSalvo)) return true;

      const payload = _.cloneDeep(this.payloadConfigs);
      const revisao = this.autosaveRevisao;
      this.autosaveEstado = "salvando";
      this.autosaveMensagem = "Salvando...";
      const envio = (async () => {
        try {
          await this.salvarConfigs(payload);
          this.autosaveUltimoSalvo = payload;
          if (_.isEqual(this.payloadConfigs, payload)) {
            this.autosaveEstado = "salvo";
            this.autosaveMensagem = "Alterações salvas";
            this.autosaveStatusTimer = setTimeout(() => {
              if (this.autosaveEstado === "salvo") this.autosaveEstado = "";
            }, 3000);
          }
          return true;
        } catch (error) {
          this.autosaveEstado = "erro";
          this.autosaveMensagem =
            error?.response?.data?.msg ||
            error?.response?.data?.message ||
            error?.message ||
            "Não foi possível salvar as configurações gerais.";
          return false;
        } finally {
          this.autosaveEmAndamento = null;
          if (
            this.autosaveRevisao > revisao &&
            !this.autosaveTimer &&
            !_.isEqual(this.payloadConfigs, this.autosaveUltimoSalvo)
          ) {
            this.agendarAutosave();
          }
        }
      })();
      this.autosaveEmAndamento = envio;
      return envio;
    },
    async flushAutosave() {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
      if (this.autosaveEmAndamento) {
        const salvo = await this.autosaveEmAndamento;
        if (!salvo) return false;
      }
      if (_.isEqual(this.payloadConfigs, this.autosaveUltimoSalvo)) return true;
      return this.executarAutosave();
    },
    async selecionarAbaConfiguracao(indice, select = null) {
      const anterior = this.abaConfiguracaoAtiva;
      if (indice === anterior) return;
      if (await this.flushAutosave()) {
        this.abaConfiguracaoAtiva = indice;
      } else if (select) {
        select.value = String(anterior);
      }
    },
    avisarSaidaComAutosavePendente(event) {
      if (
        this.autosaveReady &&
        (this.autosaveEmAndamento ||
          this.autosaveTimer ||
          !_.isEqual(this.payloadConfigs, this.autosaveUltimoSalvo))
      ) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    selecionarPassoQz(passo) {
      this.restaurante_qz_passo = passo;
      if (passo === 3 && this.restaurante_qz_verificado && !this.restaurante_impressoras_qz.length) {
        this.carregarImpressorasRestaurante(true);
      }
    },
    restauranteImpressoraDestino(destino) {
      return {
        pdv: this.impressora_pdv,
        checkout: this.restaurante_impressora_checkout,
        comprovante: this.restaurante_impressora_comprovante_cliente,
        cozinha: this.restaurante_impressora_cozinha,
        bar: this.restaurante_impressora_bar,
        delivery: this.restaurante_impressora_delivery,
      }[destino];
    },
    alterarImpressoraDestino(destino, impressora) {
      const campos = {
        pdv: "impressora_pdv",
        checkout: "restaurante_impressora_checkout",
        comprovante: "restaurante_impressora_comprovante_cliente",
        cozinha: "restaurante_impressora_cozinha",
        bar: "restaurante_impressora_bar",
        delivery: "restaurante_impressora_delivery",
      };
      this[campos[destino]] = impressora;
    },
    atualizarEstadoQz(impressoras) {
      this.restaurante_impressoras_qz = impressoras;
      this.restaurante_qz_verificado = impressoras.length > 0;
      this.restaurante_qz_status = impressoras.length
        ? `QZ conectado · operação protegida concluída · ${impressoras.length} impressora(s)`
        : "QZ conectado · nenhuma impressora encontrada neste computador";
      if (impressoras.length) this.restaurante_qz_passo = 3;
    },
    async carregarImpressorasRestaurante(silencioso = false) {
      if (this.restaurante_qz_carregando) return;
      this.restaurante_qz_carregando = true;
      this.restaurante_qz_status = "Conectando ao QZ Tray...";

      try {
        const impressoras = await listarImpressorasQz();
        this.atualizarEstadoQz(impressoras);
        if (!silencioso && !impressoras.length) {
          this.$toast.warning("Nenhuma impressora encontrada neste computador.");
        }
      } catch (error) {
        this.restaurante_qz_verificado = false;
        this.restaurante_impressoras_qz = [];
        this.restaurante_qz_status =
          error.message ||
          "Não foi possível conectar ao QZ Tray neste computador.";
        if (!silencioso) this.$toast.error(this.restaurante_qz_status);
      } finally {
        this.restaurante_qz_carregando = false;
      }
    },
    async verificarQzRestaurante() {
      if (this.restaurante_qz_carregando) return;
      this.restaurante_qz_carregando = true;
      this.restaurante_qz_verificado = false;
      this.restaurante_impressoras_qz = [];
      this.restaurante_qz_status = "Criando uma nova conexão com o QZ Tray...";
      try {
        const impressoras = await reconectarQz();
        if (!impressoras.length) {
          throw new Error("QZ Tray conectado, mas nenhuma impressora foi encontrada.");
        }
        this.atualizarEstadoQz(impressoras);
      } catch (error) {
        this.restaurante_qz_status =
          error.message || "Não foi possível verificar o QZ Tray.";
        this.$toast.error(this.restaurante_qz_status);
      } finally {
        this.restaurante_qz_carregando = false;
      }
    },
    async baixarCertificadoQz() {
      this.restaurante_qz_baixando_certificado = true;

      try {
        const { data } = await this.$http.get("/restaurante/qz/certificate");

        if (!data || !data.enabled || !data.certificate) {
          this.$toast.error(
            "Certificado do QZ Tray ainda não está disponível no servidor."
          );
          return;
        }

        const blob = new Blob([data.certificate], {
          type: "text/plain;charset=utf-8",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "nuts-qz-certificate.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$toast.success(
          "Certificado baixado. Importe-o pelo Site Manager do QZ Tray."
        );
      } catch (error) {
        this.$toast.error("Não foi possível baixar o certificado do QZ Tray.");
      } finally {
        this.restaurante_qz_baixando_certificado = false;
      }
    },
    async testarImpressaoRestaurante() {
      if (this.restaurante_qz_testando) return;
      const impressora = this.destinoTesteImpressao?.impressora;
      if (!impressora) {
        this.$toast.warning("Selecione uma impressora antes de realizar o teste.");
        return;
      }

      this.restaurante_qz_testando = true;
      try {
        await imprimirTextoQz(impressora, ticketTesteProducao(impressora));
        this.$toast.success("Teste enviado para a impressora.");
      } catch (error) {
        this.$toast.error(
          error.message === "Printer is not accepting job."
            ? `A impressora "${impressora}" recusou o teste. Verifique se está online e disponível no Windows.`
            : error.message
        );
      } finally {
        this.restaurante_qz_testando = false;
      }
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.avisarSaidaComAutosavePendente);
    document.addEventListener("pointerdown", this.fecharBuscaAoClicarFora);
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
    }
    this.getConfigurationList();
    this.contas_marcadas_color = localStorage.getItem("contas_marcadas_color");
    if (this.$route.query.aba === "balancas") {
      this.abaConfiguracaoAtiva = this.abaBalancasIndex;
    }
  },
  async beforeRouteLeave(to, from, next) {
    next((await this.flushAutosave()) ? undefined : false);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.avisarSaidaComAutosavePendente);
    document.removeEventListener("pointerdown", this.fecharBuscaAoClicarFora);
    clearTimeout(this.buscaDestaqueTimer);
    clearTimeout(this.autosaveTimer);
    clearTimeout(this.autosaveStatusTimer);
  },
  watch: {
    payloadConfigs(novo, anterior) {
      if (!this.autosaveReady || _.isEqual(novo, anterior)) return;
      this.autosaveRevisao += 1;
      const camposAlterados = Object.keys(novo).filter(
        (campo) => !_.isEqual(novo[campo], anterior?.[campo])
      );
      const somenteTextoOuNumero = camposAlterados.every((campo) =>
        CAMPOS_AUTOSAVE_COM_DEBOUNCE.has(campo)
      );
      this.agendarAutosave(somenteTextoOuNumero ? 650 : 0);
    },
    contas_marcadas_color: {
      handler(color) {
        localStorage.setItem("contas_marcadas_color", color);
      },
    },
  },
};
</script>

<style>
@media (min-width: 992px) and (min-height: 641px) {
  html:has(.config-root) {
    overflow-y: hidden;
  }

  body:has(.config-root) > .flex-root {
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  body:has(.config-root) > .flex-root > .flex-root,
  body:has(.config-root) .page,
  body:has(.config-root) #kt_wrapper,
  body:has(.config-root) #kt_content,
  body:has(.config-root) #kt_content > .flex-column-fluid,
  body:has(.config-root) #kt_content > .flex-column-fluid > .container,
  body:has(.config-root) #kt_content > .flex-column-fluid > .container > .d-lg-flex,
  body:has(.config-root) .content-wrapper {
    min-height: 0;
  }

  body:has(.config-root) > .flex-root > .flex-root,
  body:has(.config-root) .page,
  body:has(.config-root) #kt_wrapper,
  body:has(.config-root) #kt_content,
  body:has(.config-root) #kt_content > .flex-column-fluid {
    flex: 1 1 auto;
  }

  body:has(.config-root) #kt_content {
    background: #f2f3f7;
    padding-bottom: 0;
  }

  body:has(.config-root) #kt_content > .flex-column-fluid > .container {
    display: flex;
    flex-direction: column;
  }

  body:has(.config-root) #kt_content > .flex-column-fluid > .container > .d-lg-flex,
  body:has(.config-root) .content-wrapper > .config-root {
    flex: 1 1 auto;
  }

  body:has(.config-root) .content-wrapper {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  body:has(.config-root) #kt_footer {
    display: none !important;
  }
}
</style>

<style scoped>
.config-tabs ::v-deep .nav-tabs {
  display: none;
}

.config-header,
.config-sidebar,
.config-mobile-nav,
.config-column-card {
  border: 1px solid #e6eaf0;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 2px 8px rgba(39, 61, 89, 0.04);
}

.config-header {
  flex-shrink: 0;
  margin-bottom: 1.25rem;
}

.config-header-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
}

.config-autosave-status {
  color: #526277;
  font-size: 0.875rem;
  white-space: nowrap;
}

.config-autosave-error {
  color: #b54236;
  white-space: normal;
}

.config-autosave-retry {
  margin-left: 0.5rem;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.config-header .card-title {
  margin-bottom: 0;
}

.config-search {
  position: relative;
  min-width: 0;
  margin: 0 1.5rem 1.25rem;
}

.config-search-input {
  width: 100%;
}

.config-search-dropdown {
  position: absolute;
  z-index: 30;
  top: calc(100% + 0.25rem);
  right: 0;
  left: 0;
  max-height: min(22rem, 50vh);
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid #e6eaf0;
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: 0 8px 24px rgba(39, 61, 89, 0.12);
}

.config-search-result {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 0;
  border-bottom: 1px solid #f0f2f5;
  background: #fff;
  color: #172b4d;
  text-align: left;
  cursor: pointer;
}

.config-search-result:hover,
.config-search-result:focus-visible {
  background: #fff4ef;
  outline: none;
}

.config-search-result-label {
  font-weight: 600;
}

.config-search-result small,
.config-search-empty {
  color: #69778a;
}

.config-search-empty {
  padding: 0.75rem 0.9rem;
}

.config-search-highlight {
  border-radius: 0.4rem;
  background: #fff0e8;
  box-shadow: inset 0 0 0 1px #f7bd9f;
}

.config-shell {
  display: grid;
  grid-template-columns: 15rem minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  padding-bottom: 1rem;
}

.config-sidebar {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
}

.config-sidebar-item {
  width: 100%;
  padding: 0.85rem 1rem;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 0.25rem;
  background: transparent;
  color: #4b5563;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}

.config-sidebar-item:hover {
  background: #fff7f4;
  color: #b84b36;
}

.config-sidebar-item:focus-visible {
  outline: 2px solid #ed6753;
  outline-offset: 2px;
}

.config-sidebar-item-active,
.config-sidebar-item-active:hover {
  border-left-color: #ed6753;
  background: #fff0eb;
  color: #ad422d;
  font-weight: 700;
}

.config-main {
  min-width: 0;
}

.config-mobile-nav {
  display: none;
  padding: 1.5rem;
}

.config-content-frame {
  min-width: 0;
}

.config-content-body {
  min-width: 0;
  min-height: 0;
  padding: 0.125rem 0.25rem 0.5rem;
  overflow-x: hidden;
  overflow-y: auto;
}

@media (min-width: 992px) and (min-height: 641px) {
  .config-root,
  .config-page,
  .config-shell,
  .config-main,
  .config-content-frame {
    min-height: 0;
  }

  .config-root,
  .config-page,
  .config-main,
  .config-content-frame {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  }

  .config-shell {
    flex: 1 1 auto;
    align-items: stretch;
  }

  .config-sidebar {
    align-self: start;
  }

  .config-content-body {
    flex: 1 1 auto;
  }
}

.config-tab-body {
  min-width: 0;
  padding: 0.125rem 0 0.25rem;
  border: 0;
  background: transparent;
}

.config-column-layout {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  align-items: stretch;
}

.config-column-card {
  min-width: 0;
  padding: 0.75rem 1.125rem;
}

.config-column-card-single {
  width: 100%;
}

.config-column-card > .config-row {
  min-height: 3rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid #edf0f4;
}

.config-column-card > .config-row:last-child {
  border-bottom: 0;
}

.config-column-card > .config-row:not(:has(> *)) {
  display: none;
}

.config-column-card .config-row p.font-weight-bolder {
  font-size: 1rem;
  line-height: 1.35;
}

.config-column-card .config-row > [class*="col-"] {
  max-width: none;
}

.config-column-card .config-row > .row {
  max-width: none;
}

.config-row {
  min-width: 0;
  padding: 1rem 0;
  border-bottom: 1px solid #e6eaf0;
}

.config-row > [class*="col-"] {
  width: 100%;
  max-width: 48rem;
  padding: 0;
}

.config-row > .row {
  max-width: 48rem;
  margin: 0;
}

.config-row > .row > [class*="col-"] {
  min-width: 0;
  padding-left: 0;
  padding-right: 1.25rem;
}

.config-row p.font-weight-bolder {
  margin-bottom: 0.5rem !important;
  line-height: 1.45;
}

.config-column-card > .config-row > .row > [class*="col-"]:only-child:has(> p.font-weight-bolder + .switch) {
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
  padding-right: 0;
}

.config-row-caixa > .row {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
}

.config-row-caixa > .row > [class*="col-"] {
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
  padding: 0.625rem 0;
}

.config-row-caixa > .row > [class*="col-"] + [class*="col-"] {
  border-top: 1px solid #edf0f4;
}

.config-column-card > .config-row-caixa {
  padding: 0;
}

.config-column-card > .config-row-natureza > .row > [class*="col-"] {
  flex: 1 1 16rem;
  max-width: 100%;
  padding-right: 0;
}

.config-column-card > .config-row:has(input[type="color"]) > [class*="col-"],
.config-column-card > .config-row:has(> [class*="col-"] > select) > [class*="col-"] {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.config-column-card > .config-row:has(input[type="color"]) p,
.config-column-card > .config-row:has(> [class*="col-"] > select) p {
  margin: 0 !important;
}

.config-column-card > .config-row input[type="color"] {
  width: 5rem;
  height: 1.75rem;
}

.config-column-card > .config-row select {
  width: 7rem;
}

.config-row > [class*="col-"]:has(> p.font-weight-bolder + .switch),
.config-row > .row > [class*="col-"]:has(> p.font-weight-bolder + .switch) {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  column-gap: 0.75rem;
  row-gap: 0.5rem;
  min-width: 0;
}

.config-row > [class*="col-"] > p.font-weight-bolder + .switch,
.config-row > .row > [class*="col-"] > p.font-weight-bolder + .switch {
  justify-self: end;
}

.config-row > [class*="col-"] > p.font-weight-bolder:has(+ .switch),
.config-row > .row > [class*="col-"] > p.font-weight-bolder:has(+ .switch) {
  min-width: 0;
  margin: 0 !important;
  overflow-wrap: break-word;
}

.config-row > [class*="col-"]:has(> p.font-weight-bolder + .switch) > :not(p):not(.switch),
.config-row > .row > [class*="col-"]:has(> p.font-weight-bolder + .switch) > :not(p):not(.switch) {
  grid-column: 1 / -1;
}

.config-row-natureza > .row > [class*="col-"] {
  flex: 0 0 100% !important;
  width: 100%;
  max-width: 100%;
}

.config-dependent-fields {
  box-sizing: border-box;
  min-width: 0;
  max-width: calc(100% - 0.75rem) !important;
  margin: 0.625rem 0 0 0.75rem !important;
  padding: 0.625rem 0.75rem !important;
  border: 0;
  border-left: 2px solid #f1c9b9 !important;
  border-radius: 0 0.375rem 0.375rem 0;
  background: #fffaf7;
}

.config-dependent-fields .form-group {
  margin-bottom: 0;
}

.config-dependent-fields label,
.config-dependent-fields .col-form-label,
.config-dependent-fields p.font-weight-bolder {
  color: #526277 !important;
  font-size: 0.85rem !important;
}

.config-dependent-fields .form-control,
.config-input-nsu {
  min-height: 2.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.config-dependent-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(9rem, 100%), 1fr));
  gap: 0.75rem;
}

.config-row > .config-dependent-grid > [class*="col-"] {
  width: 100%;
  max-width: none;
  padding: 0;
}

.config-input-short {
  width: 8rem;
  max-width: 100%;
}

.config-column-card > .config-row > .config-inline-short-value {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.config-inline-short-value p {
  margin: 0 !important;
}

.config-input-nsu {
  width: 14rem;
  max-width: 100%;
}

.config-row > .config-message-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto max-content;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}

.config-message-line > p.font-weight-bolder {
  min-width: 0;
  margin: 0 !important;
  overflow-wrap: break-word;
}

.config-message-line > .switch {
  grid-column: 3;
  justify-self: end;
}

.config-message-action {
  grid-column: 2;
  justify-self: end;
  min-height: 2rem;
  max-width: 100%;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.2;
  white-space: nowrap;
}

.config-row-caixa > .row > .config-dependent-fields,
.printing-card .config-row > .row > .config-dependent-fields {
  border-top: 0;
}

.config-section {
  margin: 2rem 0 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #dce5f0;
  color: #3f4d63;
  font-size: 1rem;
  font-weight: 700;
}

.config-tab-body > .config-section:first-child {
  margin-top: 0;
}

.printing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.printing-card {
  min-width: 0;
  padding: 1rem 1.125rem;
  border: 1px solid #e6eaf0;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 2px 8px rgba(39, 61, 89, 0.04);
}

.printing-card-title {
  margin: 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e6eaf0;
  color: #344056;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.printing-grid .printing-card-title {
  margin-bottom: 0.125rem;
  padding-bottom: 0.875rem;
  border-bottom-color: #cbd6e2;
  color: #1d2d45;
  font-size: 1.125rem;
  font-weight: 700;
}

.printing-card .config-row {
  padding: 0.625rem 0;
  border-bottom: 1px solid #edf0f4;
}

.printing-card .config-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.printing-card .config-row p.font-weight-bolder {
  font-size: 1rem;
  font-weight: 500 !important;
  line-height: 1.35;
}

.printing-card .config-row > [class*="col-"],
.printing-card .config-row > .row > [class*="col-"] {
  width: 100%;
  max-width: none;
  padding: 0;
}

.printing-card .config-row > .row {
  display: block;
  width: 100%;
  max-width: none;
}

.printing-card .config-row > .row > [class*="col-"] + [class*="col-"] {
  margin-top: 0.7rem;
  padding-top: 0.7rem;
  border-top: 1px solid #edf0f4;
}

.qz-setup {
  max-width: 100%;
  min-width: 0;
}

.qz-global-card {
  margin-bottom: 1.25rem;
  padding: 1.125rem 1.25rem;
  box-shadow: 0 4px 14px rgba(39, 61, 89, 0.06);
}

.qz-card-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 1.125rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dce5ef;
}

.qz-card-header .printing-card-title {
  flex: 1 1 auto;
  padding: 0;
  border: 0;
  color: #1d2d45;
  font-size: 1.25rem;
}

.qz-connection-status {
  max-width: 100%;
  margin: 0 0 0 auto;
  padding: 0.375rem 0.75rem;
  border: 1px solid #dce5ef;
  border-radius: 999px;
  background: #f5f7fa;
  color: #526277;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: right;
  overflow-wrap: anywhere;
}

.qz-connection-status-connected {
  border-color: #cceadf;
  background: #f0faf5;
  color: #168477;
}

.qz-status {
  max-width: 100%;
  overflow-wrap: anywhere;
  color: #526277;
  font-size: 0.85rem;
}

.qz-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.375rem;
  border-radius: 0.5rem;
  background: #f8fafc;
}

.qz-step {
  display: flex;
  flex: 1 1 190px;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.5rem;
  border: 0;
  border-bottom: 2px solid #d7e1ef;
  background: transparent;
  color: #7b8798;
  text-align: left;
}

.qz-step-current {
  border-bottom-color: #ed6753;
  color: #b44935;
  font-weight: 600;
}

.qz-step-done {
  color: #168477;
}

.qz-step-number {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  height: 24px;
  border-radius: 50%;
  background: #a1a9b6;
  color: #fff;
}

.qz-step-current .qz-step-number {
  background: #ed6753;
}

.qz-step-done .qz-step-number {
  background: #168477;
}

.qz-step-content {
  min-width: 0;
  padding: 1.125rem;
  border: 1px solid #e6ecf2;
  border-radius: 0.625rem;
  background: #fbfcfe;
}

.qz-step-content > h6 {
  margin-bottom: 0.375rem;
  color: #25364d !important;
  font-size: 1.05rem;
}

.qz-printers-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;
}

.qz-printer-item {
  min-width: 0;
}

.qz-printer-item ::v-deep .custom-select {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  text-overflow: ellipsis;
}

.qz-printer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e6ecf2;
}

.qz-printer-action-main {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-left: auto;
}

.qz-instructions {
  padding-left: 1.4rem;
}

.qz-instructions li {
  margin-bottom: 0.45rem;
}

.qz-guide-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.qz-guide-card {
  display: block;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #dce6f1;
  border-radius: 7px;
  background: #fff;
  color: #29486e;
  text-decoration: none;
}

.qz-guide-card:hover,
.qz-guide-card:focus {
  border-color: #1765ca;
  color: #1258af;
  box-shadow: 0 2px 10px rgba(23, 101, 202, 0.12);
}

.qz-guide-image {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 190px;
  padding: 0.35rem;
  overflow: hidden;
  background: #292929;
}

.qz-guide-image img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.qz-guide-caption {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem;
  font-size: 0.82rem;
}

.qz-guide-caption small {
  display: block;
  margin-top: 0.15rem;
  color: #748297;
}

.qz-guide-number {
  display: inline-flex;
  flex: 0 0 23px;
  align-items: center;
  justify-content: center;
  height: 23px;
  border-radius: 50%;
  background: #1765ca;
  color: #fff;
  font-weight: 700;
}

.qz-guide-finish {
  padding: 0.75rem 0;
  font-size: 0.86rem;
}

.qz-guide-actions {
  gap: 0.5rem;
  padding-top: 0.75rem;
}

@media (max-width: 1199px) {
  .config-page {
    padding-inline: 1.25rem;
  }

  .config-row > .config-message-line {
    grid-template-columns: minmax(0, 1fr) max-content;
  }

  .config-message-line > .switch {
    grid-column: 2;
    grid-row: 1;
  }

  .config-message-action {
    grid-column: 1;
    grid-row: 2;
    justify-self: start;
  }

  .printing-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .qz-printers-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .qz-guide-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .qz-guide-image {
    height: 220px;
  }
}

@media (max-width: 991px) {
  .config-shell {
    grid-template-columns: minmax(0, 1fr);
  }

  .config-column-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .config-sidebar {
    display: none;
  }

  .config-mobile-nav {
    display: block;
  }

  .config-mobile-nav label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  .config-content-body {
    overflow: visible;
  }
}

@media (max-height: 640px) {
  .config-content-body {
    overflow: visible;
  }
}

@media (max-width: 767px) {
  .config-page {
    padding-inline: 1rem;
  }

  .config-search {
    margin: 0 1.25rem 1.25rem;
  }

  .config-header {
    margin-bottom: 1.25rem;
  }

  .config-header-body,
  .config-mobile-nav {
    padding: 1.25rem;
  }

  .config-shell {
    gap: 1.25rem;
  }

  .config-tab-body {
    padding-bottom: 0.5rem;
  }

  .config-column-card {
    padding: 0.875rem 1.125rem;
  }

  .config-row > .row > [class*="col-"] {
    padding-right: 0;
  }

  .qz-printers-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .qz-card-header .printing-card-title {
    flex-basis: 100%;
  }

  .qz-connection-status {
    flex-basis: auto;
    width: fit-content;
  }

  .qz-printer-action-main {
    width: 100%;
    margin-left: 0;
  }

  .qz-printer-action-main .btn {
    flex: 1 1 auto;
  }

  .config-column-card > .config-row > .config-inline-short-value {
    grid-template-columns: minmax(0, 1fr);
  }

  .config-header-body {
    align-items: flex-start;
    flex-direction: column;
  }

  .qz-printers-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .qz-steps {
    display: block;
  }

  .qz-step {
    width: 100%;
  }

  .qz-step-content {
    padding: 0.75rem;
  }

  .qz-guide-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
